import { DataService } from '../services/data.js';
import { getIcon, showToast } from '../components/ui.js';
import { injectCtasIntoEmailBody, renderCtaButtonHtml } from '../services/diagnosticHelper.js';

let activeTab = 'campaigns'; // 'campaigns' | 'templates' | 'accounts' | 'stats'
let cachedCampaigns = [];
let cachedTemplates = [];
let cachedAccounts = [];
let cachedClients = [];
let cachedContacts = [];
let cachedLogs = [];
let cachedStats = null;

// Table sorting state for individual email logs
let logSortColumn = 'sent_at';
let logSortDir = 'desc';
let logStatusFilter = 'all';
let logSearchTerm = '';

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export async function renderEmailMarketingView() {
  let campaigns = [], templates = [], accounts = [], clients = [], contacts = [], stats = null, logs = [];
  try {
    [campaigns, templates, accounts, clients, contacts, stats, logs] = await Promise.all([
      DataService.getCampaigns().catch(e => { console.error('Error fetching campaigns:', e); return []; }),
      DataService.getEmailTemplates().catch(e => { console.error('Error fetching templates:', e); return []; }),
      DataService.getEmailAccounts().catch(e => { console.error('Error fetching accounts:', e); return []; }),
      DataService.getClients().catch(e => { console.error('Error fetching clients:', e); return []; }),
      DataService.getContacts().catch(e => { console.error('Error fetching contacts:', e); return []; }),
      DataService.getEmailMarketingStats().catch(e => { console.error('Error fetching stats:', e); return null; }),
      DataService.getEmailLogs({ limit: 200 }).catch(e => { console.error('Error fetching logs:', e); return []; })
    ]);
  } catch (err) {
    console.error('Error loading email marketing data:', err);
  }

  cachedCampaigns = campaigns || [];
  cachedTemplates = templates || [];
  cachedAccounts = accounts || [];
  cachedClients = clients || [];
  cachedContacts = contacts || [];
  cachedStats = stats || {
    totalSent: 0,
    totalOpened: 0,
    totalClicked: 0,
    totalReplied: 0,
    openRate: '0.0',
    clickRate: '0.0',
    replyRate: '0.0',
    activeCampaignsCount: 0,
    ctaBreakdown: [],
    funnel: { sent: 0, replies: 0, diagnostics: 0, proposals: 0, won: 0 }
  };
  cachedLogs = logs || [];

  return `
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-3 border-b border-border">
        <div>
          <div class="flex items-center gap-2">
            <h1 class="text-2xl font-bold tracking-tight text-text-primary">Email Marketing & Comunicación</h1>
            <span class="text-xs font-mono px-2 py-0.5 rounded bg-surface-subtle border border-border">
              ${campaigns.length} Campañas • ${accounts.length} Cuentas
            </span>
          </div>
          <p class="text-xs text-text-secondary mt-0.5">Reactivación comercial, seguimiento automatizado y conversión de contactos en oportunidades</p>
        </div>

        <div class="flex flex-wrap items-center gap-2.5">
          <button id="btn-open-help-modal" class="px-3 py-2 text-xs font-semibold rounded-md border border-border bg-surface hover:bg-surface-subtle transition-colors flex items-center gap-1.5 text-text-primary shadow-sm" title="Guía de uso y configuración del módulo">
            ${getIcon('helpCircle', 'w-3.5 h-3.5 text-text-secondary')}
            <span>Guía & Ayuda</span>
          </button>
          <button id="btn-sync-replies" class="px-3 py-2 text-xs font-medium rounded-md border border-border bg-surface hover:bg-surface-subtle transition-colors flex items-center gap-2 text-text-primary" title="Consultar respuestas en buzones">
            ${getIcon('refreshCw', 'w-3.5 h-3.5 text-text-secondary')}
            <span>Sincronizar Respuestas</span>
          </button>
          <button id="btn-run-batch" class="px-3.5 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-all shadow-sm flex items-center gap-2">
            ${getIcon('send', 'w-3.5 h-3.5')}
            <span>Procesar Cola de Envíos</span>
          </button>
        </div>
      </div>

      <!-- Quick KPI Strip -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
        <div class="bg-surface p-4 rounded-md border border-border shadow-sm">
          <p class="text-[11px] font-medium text-text-secondary">Emails Enviados</p>
          <p class="text-xl font-bold text-text-primary mt-1 font-mono">${stats?.totalSent || 0}</p>
        </div>
        <div class="bg-surface p-4 rounded-md border border-border shadow-sm">
          <p class="text-[11px] font-medium text-text-secondary">Tasa de Apertura</p>
          <div class="flex items-baseline gap-1.5 mt-1">
            <p class="text-xl font-bold text-text-primary font-mono">${stats?.openRate || '0.0'}%</p>
            <span class="text-[11px] text-text-secondary font-mono">(${stats?.totalOpened || 0})</span>
          </div>
        </div>
        <div class="bg-surface p-4 rounded-md border border-border shadow-sm">
          <p class="text-[11px] font-medium text-text-secondary">Clicks en CTA</p>
          <div class="flex items-baseline gap-1.5 mt-1">
            <p class="text-xl font-bold text-text-primary font-mono">${stats?.clickRate || '0.0'}%</p>
            <span class="text-[11px] text-text-secondary font-mono">(${stats?.totalClicked || 0})</span>
          </div>
        </div>
        <div class="bg-surface p-4 rounded-md border border-border shadow-sm">
          <p class="text-[11px] font-medium text-text-secondary">Respuestas Comerciales</p>
          <div class="flex items-baseline gap-1.5 mt-1">
            <p class="text-xl font-bold text-emerald-600 font-mono">${stats?.replyRate || '0.0'}%</p>
            <span class="text-[11px] text-text-secondary font-mono">(${stats?.totalReplied || 0})</span>
          </div>
        </div>
      </div>

      <!-- Navigation Tabs -->
      <div class="flex border-b border-border gap-2 text-xs font-semibold">
        <button class="tab-btn px-4 py-2.5 border-b-2 transition-colors ${activeTab === 'campaigns' ? 'border-primary text-text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'}" data-tab="campaigns">
          Campañas & Secuencias (${campaigns.length})
        </button>
        <button class="tab-btn px-4 py-2.5 border-b-2 transition-colors ${activeTab === 'templates' ? 'border-primary text-text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'}" data-tab="templates">
          Plantillas & CTAs (${templates.length})
        </button>
        <button class="tab-btn px-4 py-2.5 border-b-2 transition-colors ${activeTab === 'accounts' ? 'border-primary text-text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'}" data-tab="accounts">
          Cuentas & Cuotas (${accounts.length})
        </button>
        <button class="tab-btn px-4 py-2.5 border-b-2 transition-colors ${activeTab === 'stats' ? 'border-primary text-text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'}" data-tab="stats">
          Estadísticas & Auditoría
        </button>
      </div>

      <!-- Tab Content Area -->
      <div id="email-marketing-tab-content">
        ${renderActiveTabContent()}
      </div>
    </div>

    <!-- Container for modals -->
    <div id="marketing-modal-container"></div>
  `;
}

function renderActiveTabContent() {
  switch (activeTab) {
    case 'campaigns':
      return renderCampaignsTab();
    case 'templates':
      return renderTemplatesTab();
    case 'accounts':
      return renderAccountsTab();
    case 'stats':
      return renderStatsTab();
    default:
      return renderCampaignsTab();
  }
}

// -------------------------------------------------------------
// TAB 1: CAMPAÑAS & SECUENCIAS
// -------------------------------------------------------------
function renderCampaignsTab() {
  return `
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-sm font-bold text-text-primary uppercase tracking-wider">Campañas Comerciales</h2>
          <p class="text-xs text-text-secondary">Secuencias de prospección y reactivación programadas</p>
        </div>
        <button id="btn-open-campaign-modal" class="px-3 py-1.5 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover flex items-center gap-1.5 shadow-sm">
          ${getIcon('plus', 'w-3.5 h-3.5')}
          <span>Nueva Campaña</span>
        </button>
      </div>

      ${cachedCampaigns.length === 0 ? `
        <div class="bg-surface rounded-md border border-dashed border-border p-12 text-center space-y-3">
          <div class="w-10 h-10 rounded-full bg-surface-subtle border border-border flex items-center justify-center mx-auto text-text-secondary">
            ${getIcon('mail', 'w-5 h-5')}
          </div>
          <h3 class="text-sm font-semibold text-text-primary">No hay campañas creadas aún</h3>
          <p class="text-xs text-text-secondary max-w-sm mx-auto">Crea tu primera campaña para reactivar prospectos o dar seguimiento automático a propuestas.</p>
          <button id="btn-empty-create-campaign" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover inline-flex items-center gap-1.5 shadow-sm">
            ${getIcon('plus', 'w-3.5 h-3.5')}
            <span>Crear Primera Campaña</span>
          </button>
        </div>
      ` : `
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          ${cachedCampaigns.map(c => {
            const statusColors = {
              draft: 'bg-zinc-100 text-zinc-700 border-zinc-300',
              scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
              running: 'bg-emerald-50 text-emerald-700 border-emerald-200',
              paused: 'bg-amber-50 text-amber-700 border-amber-200',
              completed: 'bg-purple-50 text-purple-700 border-purple-200',
              cancelled: 'bg-red-50 text-red-700 border-red-200'
            };
            const statusLabels = {
              draft: 'Borrador',
              scheduled: 'Programada',
              running: 'En ejecución',
              paused: 'Pausada',
              completed: 'Finalizada',
              cancelled: 'Cancelada'
            };

            return `
              <div class="bg-surface rounded-md border border-border p-5 space-y-3.5 flex flex-col justify-between hover:border-zinc-400 transition-all shadow-sm">
                <div>
                  <div class="flex items-start justify-between gap-2">
                    <span class="text-[10px] font-semibold font-mono uppercase px-2 py-0.5 rounded border ${statusColors[c.status] || statusColors.draft}">
                      ${statusLabels[c.status] || c.status}
                    </span>
                    <span class="text-[11px] font-mono text-text-secondary">
                      ${c.account ? c.account.email_address : 'Sin cuenta'}
                    </span>
                  </div>

                  <h3 class="text-sm font-bold text-text-primary mt-2 leading-snug">${c.name}</h3>
                  <p class="text-xs text-text-secondary mt-1 line-clamp-2">${c.objective || 'Sin objetivo comercial especificado'}</p>
                </div>

                <div class="space-y-2 pt-2 border-t border-border/70 text-xs">
                  <div class="grid grid-cols-3 gap-2 text-center">
                    <div class="bg-surface-subtle p-2 rounded border border-border/50">
                      <span class="text-[10px] text-text-secondary block">Destinatarios</span>
                      <span class="font-bold font-mono text-text-primary">${c.total_recipients || 0}</span>
                    </div>
                    <div class="bg-surface-subtle p-2 rounded border border-border/50">
                      <span class="text-[10px] text-text-secondary block">Enviados</span>
                      <span class="font-bold font-mono text-text-primary">${c.active_or_done || 0}</span>
                    </div>
                    <div class="bg-surface-subtle p-2 rounded border border-border/50">
                      <span class="text-[10px] text-text-secondary block">Respuestas</span>
                      <span class="font-bold font-mono text-emerald-600">${c.replied_count || 0}</span>
                    </div>
                  </div>

                  <div class="flex items-center justify-between text-[11px] text-text-secondary pt-1">
                    <span>Secuencia: ${c.steps?.length || 1} pasos</span>
                    <span>${c.start_at ? new Date(c.start_at).toLocaleDateString('es-AR') : 'Inmediata'}</span>
                  </div>
                </div>

                <div class="flex items-center justify-end gap-2 pt-2 border-t border-border/70">
                  ${c.status === 'running' ? `
                    <button class="btn-pause-campaign p-1.5 text-text-secondary hover:text-amber-600 rounded border border-border hover:bg-amber-50 text-xs flex items-center gap-1" data-id="${c.id}" title="Pausar envíos">
                      ${getIcon('pause', 'w-3 h-3')}
                      <span>Pausar</span>
                    </button>
                  ` : c.status === 'paused' ? `
                    <button class="btn-resume-campaign p-1.5 text-text-secondary hover:text-emerald-600 rounded border border-border hover:bg-emerald-50 text-xs flex items-center gap-1" data-id="${c.id}" title="Reanudar">
                      ${getIcon('play', 'w-3 h-3')}
                      <span>Reanudar</span>
                    </button>
                  ` : ''}

                  <button class="btn-delete-campaign p-1.5 text-text-secondary hover:text-red-600 rounded border border-border hover:bg-red-50 text-xs" data-id="${c.id}" title="Eliminar campaña">
                    ${getIcon('trash', 'w-3 h-3')}
                  </button>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      `}
    </div>
  `;
}

// -------------------------------------------------------------
// TAB 2: PLANTILLAS & CTAs (Editor Markdown/HTML & Live Preview)
// -------------------------------------------------------------
function renderTemplatesTab() {
  return `
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-sm font-bold text-text-primary uppercase tracking-wider">Plantillas de Email</h2>
          <p class="text-xs text-text-secondary">Mensajes reutilizables con variables dinámicas y botones CTA opcionales</p>
        </div>
        <button id="btn-new-template" class="px-3 py-1.5 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover flex items-center gap-1.5 shadow-sm">
          ${getIcon('plus', 'w-3.5 h-3.5')}
          <span>Nueva Plantilla</span>
        </button>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <!-- List of Templates -->
        <div class="space-y-2.5">
          ${cachedTemplates.map(t => {
            const hasCta = Array.isArray(t.ctas) && t.ctas.length > 0;
            return `
              <div class="template-item bg-surface p-3.5 rounded-md border border-border hover:border-zinc-400 cursor-pointer transition-all space-y-1.5" data-id="${t.id}">
                <div class="flex items-center justify-between">
                  <h4 class="text-xs font-bold text-text-primary">${t.name}</h4>
                  <span class="text-[10px] font-mono px-1.5 py-0.5 rounded ${hasCta ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-surface-subtle text-text-secondary border border-border'}">
                    ${hasCta ? `${t.ctas.length} CTA` : 'Sin CTA'}
                  </span>
                </div>
                <p class="text-[11px] font-mono text-text-secondary truncate">${t.subject}</p>
                <div class="flex items-center justify-between pt-2 text-[10px] text-text-secondary border-t border-border/50">
                  <span>Modificado: ${new Date(t.updated_at || t.created_at).toLocaleDateString('es-AR')}</span>
                  <div class="flex items-center gap-1">
                    <button class="btn-preview-card-template text-text-secondary hover:text-primary p-1 rounded hover:bg-surface-subtle" data-id="${t.id}" title="Vista previa completa">
                      ${getIcon('maximize', 'w-3 h-3')}
                    </button>
                    <button class="btn-delete-template text-text-secondary hover:text-red-600 p-1" data-id="${t.id}" title="Eliminar plantilla">
                      ${getIcon('trash', 'w-3 h-3')}
                    </button>
                  </div>
                </div>
              </div>
            `;
          }).join('')}
        </div>

        <!-- Template Editor -->
        <div class="lg:col-span-2 bg-surface rounded-md border border-border p-5 space-y-4 shadow-sm" id="template-editor-container">
          <div class="flex items-center justify-between border-b border-border pb-3">
            <div>
              <h3 class="text-xs font-bold uppercase tracking-wider text-text-primary" id="editor-title">Nueva Plantilla</h3>
              <p class="text-[11px] text-text-secondary">Redacta el mensaje y añade variables dinámicas personalizadas</p>
            </div>
            <div class="flex items-center gap-1.5 text-text-secondary text-[11px]">
              <span>Tags rápidos:</span>
              <button type="button" class="btn-insert-var px-1.5 py-0.5 rounded border border-border bg-surface-subtle hover:bg-surface font-mono text-[10px]" data-var="{{contact.first_name}}">Nombre</button>
              <button type="button" class="btn-insert-var px-1.5 py-0.5 rounded border border-border bg-surface-subtle hover:bg-surface font-mono text-[10px]" data-var="{{company.name}}">Empresa</button>
              <button type="button" class="btn-insert-var px-1.5 py-0.5 rounded border border-border bg-surface-subtle hover:bg-surface font-mono text-[10px]" data-var="{{sender.name}}">Remitente</button>
            </div>
          </div>

          <form id="form-template-editor" class="space-y-3.5 text-xs">
            <input type="hidden" id="tpl-id" value="" />

            <div>
              <label class="block font-semibold text-text-primary mb-1">Nombre Interno de la Plantilla</label>
              <input type="text" id="tpl-name" required placeholder="Ej: Reactivación Q3 - Diagnóstico" class="w-full p-2 bg-surface-subtle border border-border rounded outline-none" />
            </div>

            <div>
              <label class="block font-semibold text-text-primary mb-1">Línea de Asunto</label>
              <input type="text" id="tpl-subject" required placeholder="Ej: {{contact.first_name}}, una consulta breve sobre {{company.name}}" class="w-full p-2 bg-surface-subtle border border-border rounded outline-none font-mono" />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Code/Markdown/HTML input -->
              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="font-semibold text-text-primary">Cuerpo del Correo (HTML o Markdown)</label>
                  <span class="text-[10px] text-text-secondary font-mono">HTML permitido</span>
                </div>
                <!-- Quick HTML Formatting Toolbar -->
                <div class="flex flex-wrap items-center gap-1 mb-1.5 p-1 bg-surface-subtle rounded border border-border/70 text-[10px]">
                  <span class="text-text-secondary font-medium px-1">Formato:</span>
                  <button type="button" class="btn-insert-html px-1.5 py-0.5 rounded bg-surface border border-border hover:bg-surface-subtle font-mono text-text-primary font-bold" data-open="<strong>" data-close="</strong>" title="Negrita">B</button>
                  <button type="button" class="btn-insert-html px-1.5 py-0.5 rounded bg-surface border border-border hover:bg-surface-subtle font-mono text-text-primary italic" data-open="<em>" data-close="</em>" title="Cursiva">I</button>
                  <button type="button" class="btn-insert-html px-1.5 py-0.5 rounded bg-surface border border-border hover:bg-surface-subtle font-mono text-text-primary" data-open="<h3>" data-close="</h3>" title="Título H3">H3</button>
                  <button type="button" class="btn-insert-html px-1.5 py-0.5 rounded bg-surface border border-border hover:bg-surface-subtle font-mono text-text-primary" data-open="<ul>&#10;  <li>" data-close="</li>&#10;</ul>" title="Lista">• Lista</button>
                  <button type="button" class="btn-insert-html px-1.5 py-0.5 rounded bg-surface border border-border hover:bg-surface-subtle font-mono text-text-primary" data-open="<hr style='border:0;border-top:1px solid #e4e4e7;margin:16px 0;'/>" data-close="" title="Separador">Línea</button>
                  <button type="button" class="btn-insert-html px-1.5 py-0.5 rounded bg-surface border border-border hover:bg-surface-subtle font-mono text-text-primary" data-open="<div style='background:#f4f4f5;padding:10px 14px;border-radius:6px;border-left:3px solid #18181b;margin:12px 0;'>&#10;" data-close="&#10;</div>" title="Caja destacada">Caja</button>
                  <span class="text-border mx-0.5">|</span>
                  <button type="button" id="btn-insert-cta-tag" class="px-2 py-0.5 rounded bg-[#D2F26B] border border-[#B9DC45] hover:bg-[#E0F98F] text-[#0D1A14] font-bold font-mono flex items-center gap-1 shadow-xs" title="Insertar tag {{cta}} en la posición del cursor">
                    <span>⚡ + {{cta}}</span>
                  </button>
                  <button type="button" id="btn-insert-resource-modal" class="px-1.5 py-0.5 rounded bg-surface border border-border hover:bg-surface-subtle font-mono text-primary font-semibold flex items-center gap-1" title="Insertar imagen o archivo desde Recursos">
                    ${getIcon('image', 'w-3 h-3')}
                    <span>+ Recurso</span>
                  </button>
                </div>
                <textarea id="tpl-body" rows="9" required placeholder="Hola {{contact.first_name}},&#10;&#10;Te escribo para..." class="w-full p-2.5 bg-surface-subtle border border-border rounded outline-none font-mono text-xs leading-relaxed"></textarea>
              </div>

              <!-- Live Preview Panel -->
              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="font-semibold text-text-primary">Vista Previa en Vivo</label>
                  <div class="flex items-center gap-2">
                    <button type="button" id="btn-full-preview" class="text-[11px] font-semibold text-primary hover:text-primary-hover flex items-center gap-1 hover:underline cursor-pointer">
                      ${getIcon('maximize', 'w-3 h-3')}
                      <span>Vista Completa</span>
                    </button>
                    <span class="text-[10px] text-text-secondary font-mono">Datos simulados</span>
                  </div>
                </div>
                <div id="tpl-preview-box" class="h-[175px] overflow-y-auto p-3 bg-white border border-border rounded text-xs leading-relaxed space-y-1.5 shadow-inner">
                  <p class="text-text-secondary italic">Escribe en el editor para previsualizar...</p>
                </div>
              </div>
            </div>

            <!-- Call To Action (CTA) Section -->
            <div class="p-3 bg-surface-subtle rounded border border-border/80 space-y-2">
              <div class="flex items-center justify-between">
                <div>
                  <span class="font-semibold text-text-primary text-xs">Botón de Llamado a la Acción (CTA Opcional)</span>
                  <p class="text-[11px] text-text-secondary">Enlace con tracking automático de clicks para medir interés</p>
                </div>
                <button type="button" id="btn-toggle-cta" class="text-xs font-semibold text-primary hover:underline">
                  + Configurar CTA
                </button>
              </div>
              <div id="cta-inputs-container" class="space-y-2.5 pt-2 border-t border-border/50 hidden">
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div class="sm:col-span-1">
                    <label class="block text-[11px] font-medium text-text-secondary mb-1">Texto del Botón</label>
                    <input type="text" id="cta-text" placeholder="Ej: Agendar diagnóstico breve" class="w-full text-xs p-1.5 bg-surface border border-border rounded outline-none" />
                  </div>
                  <div class="sm:col-span-1">
                    <label class="block text-[11px] font-medium text-text-secondary mb-1">URL Destino (tracking auto)</label>
                    <input type="url" id="cta-url" placeholder="https://calendly.com/mi-reunion" class="w-full text-xs p-1.5 bg-surface border border-border rounded outline-none font-mono" />
                  </div>
                  <div class="sm:col-span-1">
                    <label class="block text-[11px] font-medium text-text-secondary mb-1">Estilo Visual Editorial (DESIGN.md)</label>
                    <select id="cta-style" class="w-full text-xs p-1.5 bg-surface border border-border rounded outline-none font-semibold">
                      <option value="acid" selected>⚡ Acid Lime (#D2F26B) - Alta Energía</option>
                      <option value="forest">🌲 Forest Green (#0F3B2C) - Primario</option>
                      <option value="ink">✒️ Ink Dark (#0D1A14) - Editorial</option>
                      <option value="outline">◻️ Outline (#0D1A14 con borde)</option>
                    </select>
                  </div>
                </div>

                <div class="p-2 bg-surface rounded border border-border/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 text-[11px] text-text-secondary">
                  <span>💡 <b>Ubicación:</b> Escribe <code class="px-1 py-0.5 rounded bg-surface-subtle font-mono text-primary font-bold">{{cta}}</code> en cualquier parte del cuerpo del texto para insertar este botón exactamente ahí.</span>
                  <button type="button" id="btn-copy-cta-tag" class="px-2 py-0.5 rounded border border-border hover:bg-surface-subtle font-mono text-[10px] text-text-primary self-end sm:self-auto">
                    Insertar en cursor
                  </button>
                </div>
              </div>
            </div>

            <div class="flex justify-end gap-2 pt-2">
              <button type="button" id="btn-reset-template-form" class="px-3 py-1.5 text-xs font-medium border border-border rounded bg-surface hover:bg-surface-subtle text-text-secondary">
                Limpiar
              </button>
              <button type="submit" class="px-4 py-1.5 text-xs font-semibold rounded bg-primary text-white hover:bg-primary-hover shadow-sm">
                Guardar Plantilla
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  `;
}

// -------------------------------------------------------------
// TAB 3: CUENTAS & CUOTAS DE ENVÍO
// -------------------------------------------------------------
function renderAccountsTab() {
  return `
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-sm font-bold text-text-primary uppercase tracking-wider">Cuentas de Envío de Google</h2>
          <p class="text-xs text-text-secondary">Configuración mediante Service Account (Google Workspace y Cuentas Personales) con control de cuota diaria</p>
        </div>
        <button id="btn-open-account-modal" class="px-3 py-1.5 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover flex items-center gap-1.5 shadow-sm">
          ${getIcon('plus', 'w-3.5 h-3.5')}
          <span>Conectar Cuenta</span>
        </button>
      </div>

      ${cachedAccounts.length === 0 ? `
        <div class="bg-surface rounded-md border border-dashed border-border p-12 text-center space-y-3">
          <div class="w-10 h-10 rounded-full bg-surface-subtle border border-border flex items-center justify-center mx-auto text-text-secondary">
            ${getIcon('settings', 'w-5 h-5')}
          </div>
          <h3 class="text-sm font-semibold text-text-primary">No hay cuentas de email configuradas</h3>
          <p class="text-xs text-text-secondary max-w-sm mx-auto">Conecta tu cuenta de Google Workspace o personal pegando la clave JSON de tu Service Account para comenzar.</p>
          <button id="btn-empty-create-account" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover inline-flex items-center gap-1.5 shadow-sm">
            ${getIcon('plus', 'w-3.5 h-3.5')}
            <span>Conectar Primera Cuenta</span>
          </button>
        </div>
      ` : `
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          ${cachedAccounts.map(acc => {
            const limit = acc.daily_limit || 100;
            const sent = acc.sent_today || 0;
            const pct = Math.min(Math.round((sent / limit) * 100), 100);
            const isWarning = pct >= 80;
            const isPaused = acc.status === 'paused' || pct >= 100;

            return `
              <div class="bg-surface rounded-md border border-border p-5 space-y-3.5 shadow-sm">
                <div class="flex items-start justify-between">
                  <div class="flex items-center gap-2.5">
                    <div class="w-9 h-9 rounded-full bg-surface-subtle border border-border flex items-center justify-center font-bold text-xs text-text-primary font-mono">
                      ${acc.email_address[0].toUpperCase()}
                    </div>
                    <div>
                      <h3 class="text-xs font-bold text-text-primary">${acc.name}</h3>
                      <p class="text-[11px] font-mono text-text-secondary">${acc.email_address}</p>
                    </div>
                  </div>

                  <span class="text-[10px] font-mono font-semibold px-2 py-0.5 rounded border ${
                    isPaused ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  }">
                    ${isPaused ? 'Cuota Límite / Pausada' : 'Activa / Normal'}
                  </span>
                </div>

                <!-- Daily Quota Meter -->
                <div class="space-y-1.5 pt-2 border-t border-border/60">
                  <div class="flex items-center justify-between text-xs">
                    <span class="text-text-secondary font-medium">Consumo de cuota hoy</span>
                    <span class="font-mono font-bold ${isWarning ? 'text-amber-600' : 'text-text-primary'}">
                      ${sent} / ${limit} envíos (${pct}%)
                    </span>
                  </div>
                  <div class="w-full bg-surface-subtle rounded-full h-2 overflow-hidden border border-border/50">
                    <div class="h-full rounded-full transition-all ${
                      pct >= 90 ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500'
                    }" style="width: ${pct}%"></div>
                  </div>
                </div>

                <!-- Warning if personal gmail is using service account -->
                ${acc.email_address.endsWith('@gmail.com') && acc.auth_type === 'service_account' ? `
                  <div class="p-3 bg-red-50/80 border border-red-200 rounded-md text-red-800 text-[11px] space-y-1">
                    <p class="font-bold flex items-center gap-1.5 text-red-900">
                      <span>⚠️ Configuración Incompatible</span>
                    </p>
                    <p class="text-[10px] leading-relaxed">
                      Google <strong>bloquea las Service Accounts</strong> para casillas personales <code>@gmail.com</code> (las reserva solo para Google Workspace). Para que tus correos salgan, haz clic en <strong>Configurar</strong> y selecciona <strong>Contraseña de Aplicación</strong>.
                    </p>
                  </div>
                ` : ''}

                <div class="flex items-center justify-between text-[11px] text-text-secondary pt-2 border-t border-border/60">
                  <span>Tipo: ${acc.auth_type === 'service_account' ? 'Google Service Account' : 'App Password / SMTP'}</span>
                  <div class="flex items-center gap-2">
                    <button class="btn-edit-account text-xs font-semibold text-primary hover:underline" data-id="${acc.id}">
                      Configurar
                    </button>
                    <button class="btn-toggle-account-pause text-xs font-semibold ${acc.status === 'paused' ? 'text-emerald-600' : 'text-amber-600'} hover:underline" data-id="${acc.id}" data-status="${acc.status}">
                      ${acc.status === 'paused' ? 'Reactivar' : 'Pausar'}
                    </button>
                    <button class="btn-delete-account text-text-secondary hover:text-red-600 p-1" data-id="${acc.id}" title="Eliminar cuenta">
                      ${getIcon('trash', 'w-3 h-3')}
                    </button>
                  </div>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      `}
    </div>
  `;
}

// -------------------------------------------------------------
// TAB 4: ESTADÍSTICAS & TABLA ORDENABLE DE ENVÍOS INDIVIDUALES
// -------------------------------------------------------------
function renderStatsTab() {
  const funnel = cachedStats?.funnel || { sent: 0, replies: 0, diagnostics: 0, proposals: 0, won: 0 };
  const ctaList = cachedStats?.ctaBreakdown || [];

  // Filter and sort logs
  let displayLogs = [...cachedLogs];
  if (logStatusFilter !== 'all') {
    displayLogs = displayLogs.filter(l => l.status === logStatusFilter);
  }
  if (logSearchTerm) {
    const term = logSearchTerm.toLowerCase();
    displayLogs = displayLogs.filter(l => 
      (l.contact?.name || '').toLowerCase().includes(term) ||
      (l.client?.name || '').toLowerCase().includes(term) ||
      (l.subject || '').toLowerCase().includes(term)
    );
  }

  // Sort logs
  displayLogs.sort((a, b) => {
    let valA = a[logSortColumn];
    let valB = b[logSortColumn];
    if (logSortColumn === 'contact') {
      valA = a.contact?.name || '';
      valB = b.contact?.name || '';
    } else if (logSortColumn === 'opens') {
      valA = a.open_count || 0;
      valB = b.open_count || 0;
    }
    if (valA < valB) return logSortDir === 'asc' ? -1 : 1;
    if (valA > valB) return logSortDir === 'asc' ? 1 : -1;
    return 0;
  });

  return `
    <div class="space-y-6">
      ${funnel.sent === 0 ? `
        <div class="p-4 bg-surface-subtle border border-border rounded-md text-xs text-text-secondary flex items-center gap-3 shadow-sm">
          <span class="p-1 rounded bg-white border border-border">${getIcon('helpCircle', 'w-4 h-4 text-primary shrink-0')}</span>
          <div>
            <p class="font-bold text-text-primary">Módulo listo para operar:</p>
            <p>Aún no se han enviado correos en este módulo. Las métricas de apertura, clicks y embudo comercial se actualizarán automáticamente cuando lances tus primeras campañas o envíes reactivaciones directas.</p>
          </div>
        </div>
      ` : ''}

      <!-- Funnel & Template Breakdown Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <!-- Email Conversion Funnel (5 Coherent Marketing Stages) -->
        <div class="lg:col-span-2 bg-surface rounded-md border border-border p-5 space-y-4 shadow-sm">
          <div class="flex items-center justify-between border-b border-border pb-2.5">
            <div>
              <h3 class="text-xs font-bold uppercase tracking-wider text-text-primary">Embudo de Conversión de Email Marketing</h3>
              <p class="text-[11px] text-text-secondary">Evolución de contactos: desde el envío hasta la conversión comercial en CRM</p>
            </div>
            <span class="text-xs font-mono font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded border border-emerald-200">
              ${funnel.won || 0} Ganados en CRM
            </span>
          </div>

          <!-- Step Bars -->
          <div class="space-y-3.5 pt-1">
            <!-- 1. Enviados -->
            <div class="space-y-1">
              <div class="flex justify-between text-xs font-medium">
                <span class="text-text-primary font-semibold">1. Correos Enviados (Prospección)</span>
                <span class="font-mono font-bold">${funnel.sent} (100%)</span>
              </div>
              <div class="w-full bg-surface-subtle h-2.5 rounded-full overflow-hidden border border-border/40">
                <div class="bg-zinc-800 h-full rounded-full" style="width: ${funnel.sent > 0 ? 100 : 0}%"></div>
              </div>
            </div>

            <!-- 2. Abiertos -->
            <div class="space-y-1">
              <div class="flex justify-between text-xs font-medium">
                <span class="text-text-primary font-semibold">2. Correos Abiertos (Lectura)</span>
                <span class="font-mono font-bold text-zinc-700">
                  ${funnel.opened ?? cachedStats?.totalOpened ?? 0}
                  <span class="text-[11px] font-normal text-text-secondary">(${funnel.sent > 0 ? (((funnel.opened ?? cachedStats?.totalOpened ?? 0) / funnel.sent) * 100).toFixed(1) : 0}%)</span>
                </span>
              </div>
              <div class="w-full bg-surface-subtle h-2.5 rounded-full overflow-hidden border border-border/40">
                <div class="bg-blue-500 h-full rounded-full" style="width: ${funnel.sent > 0 ? (((funnel.opened ?? cachedStats?.totalOpened ?? 0) / funnel.sent) * 100) : 0}%"></div>
              </div>
            </div>

            <!-- 3. Clickeados -->
            <div class="space-y-1">
              <div class="flex justify-between text-xs font-medium">
                <span class="text-text-primary font-semibold">3. Clicks en CTA / Enlaces (Interés Activo)</span>
                <span class="font-mono font-bold text-blue-600">
                  ${funnel.clicked ?? cachedStats?.totalClicked ?? 0}
                  <span class="text-[11px] font-normal text-text-secondary">(${funnel.sent > 0 ? (((funnel.clicked ?? cachedStats?.totalClicked ?? 0) / funnel.sent) * 100).toFixed(1) : 0}%)</span>
                </span>
              </div>
              <div class="w-full bg-surface-subtle h-2.5 rounded-full overflow-hidden border border-border/40">
                <div class="bg-indigo-500 h-full rounded-full" style="width: ${funnel.sent > 0 ? (((funnel.clicked ?? cachedStats?.totalClicked ?? 0) / funnel.sent) * 100) : 0}%"></div>
              </div>
            </div>

            <!-- 4. Respuestas -->
            <div class="space-y-1">
              <div class="flex justify-between text-xs font-medium">
                <span class="text-text-primary font-semibold">4. Respuestas Comerciales (Conversaciones)</span>
                <span class="font-mono font-bold text-emerald-600">
                  ${funnel.replies ?? cachedStats?.totalReplied ?? 0}
                  <span class="text-[11px] font-normal text-text-secondary">(${funnel.sent > 0 ? (((funnel.replies ?? cachedStats?.totalReplied ?? 0) / funnel.sent) * 100).toFixed(1) : 0}%)</span>
                </span>
              </div>
              <div class="w-full bg-surface-subtle h-2.5 rounded-full overflow-hidden border border-border/40">
                <div class="bg-emerald-500 h-full rounded-full" style="width: ${funnel.sent > 0 ? (((funnel.replies ?? cachedStats?.totalReplied ?? 0) / funnel.sent) * 100) : 0}%"></div>
              </div>
            </div>

            <!-- 5. Ganados CRM -->
            <div class="space-y-1">
              <div class="flex justify-between text-xs font-medium">
                <span class="text-text-primary font-bold">5. Oportunidades Ganadas / Clientes Convertidos</span>
                <span class="font-mono font-bold text-emerald-700">
                  ${funnel.won || 0}
                  <span class="text-[11px] font-normal text-text-secondary">(${funnel.sent > 0 ? (((funnel.won || 0) / funnel.sent) * 100).toFixed(1) : 0}% sobre total)</span>
                </span>
              </div>
              <div class="w-full bg-surface-subtle h-2.5 rounded-full overflow-hidden border border-border/40">
                <div class="bg-emerald-600 h-full rounded-full" style="width: ${funnel.sent > 0 ? ((funnel.won / funnel.sent) * 100) : 0}%"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Rendimiento por Plantilla -->
        <div class="bg-surface rounded-md border border-border p-5 space-y-3.5 shadow-sm">
          <div class="border-b border-border pb-2.5 flex items-center justify-between">
            <div>
              <h3 class="text-xs font-bold uppercase tracking-wider text-text-primary">Rendimiento por Plantilla</h3>
              <p class="text-[11px] text-text-secondary">Efectividad comparativa de cada plantilla</p>
            </div>
            <span class="text-[10px] font-mono font-medium px-2 py-0.5 rounded bg-surface-subtle border border-border">
              ${(cachedStats?.templateBreakdown || []).length} plantillas
            </span>
          </div>

          ${(cachedStats?.templateBreakdown || []).length === 0 ? `
            <p class="text-xs text-text-secondary italic py-6 text-center">No hay envíos registrados por plantilla aún.</p>
          ` : `
            <div class="space-y-2.5 max-h-[300px] overflow-y-auto pr-1">
              ${(cachedStats.templateBreakdown || []).map(t => `
                <div class="p-2.5 rounded bg-surface-subtle border border-border/70 space-y-2">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <h4 class="text-xs font-bold text-text-primary truncate">${t.name}</h4>
                      <p class="text-[10px] text-text-secondary truncate font-mono">${t.subject || 'Sin asunto'}</p>
                    </div>
                    <span class="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-white border border-border shrink-0">
                      ${t.sent} envíos
                    </span>
                  </div>

                  <div class="grid grid-cols-3 gap-1 pt-1 border-t border-border/50 text-[10px] text-center">
                    <div class="bg-white p-1 rounded border border-border/60">
                      <span class="text-text-secondary block text-[9px]">Aperturas</span>
                      <span class="font-bold font-mono text-zinc-800">${t.openRate}%</span>
                      <span class="text-[9px] text-text-secondary">(${t.opened})</span>
                    </div>
                    <div class="bg-white p-1 rounded border border-border/60">
                      <span class="text-text-secondary block text-[9px]">Clicks</span>
                      <span class="font-bold font-mono text-blue-600">${t.clickRate}%</span>
                      <span class="text-[9px] text-text-secondary">(${t.clicked})</span>
                    </div>
                    <div class="bg-white p-1 rounded border border-border/60">
                      <span class="text-text-secondary block text-[9px]">Respuestas</span>
                      <span class="font-bold font-mono text-emerald-600">${t.replyRate}%</span>
                      <span class="text-[9px] text-text-secondary">(${t.replied})</span>
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
          `}
        </div>
      </div>

      <!-- Individual Email Activity Table (Sortable & Filterable) -->
      <div class="bg-surface rounded-md border border-border p-5 space-y-4 shadow-sm">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-border pb-3">
          <div>
            <h3 class="text-xs font-bold uppercase tracking-wider text-text-primary">Auditoría Individual de Envíos</h3>
            <p class="text-[11px] text-text-secondary">Listado detallado de cada correo enviado con sus métricas específicas</p>
          </div>

          <!-- Controls -->
          <div class="flex flex-wrap items-center gap-2.5">
            <!-- Search -->
            <input 
              type="text" 
              id="input-log-search" 
              value="${logSearchTerm}" 
              placeholder="Buscar destinatario o asunto..." 
              class="text-xs px-2.5 py-1.5 bg-surface-subtle border border-border rounded focus:bg-surface outline-none w-48"
            />

            <!-- Filter Status -->
            <select id="select-log-status" class="text-xs px-2.5 py-1.5 bg-surface-subtle border border-border rounded outline-none font-medium">
              <option value="all" ${logStatusFilter === 'all' ? 'selected' : ''}>Todos los estados</option>
              <option value="sent" ${logStatusFilter === 'sent' ? 'selected' : ''}>Enviado</option>
              <option value="opened" ${logStatusFilter === 'opened' ? 'selected' : ''}>Abierto</option>
              <option value="clicked" ${logStatusFilter === 'clicked' ? 'selected' : ''}>Clickeado</option>
              <option value="replied" ${logStatusFilter === 'replied' ? 'selected' : ''}>Respondido</option>
            </select>

            <!-- Sync Replies Button -->
            <button 
              id="btn-sync-replies-table" 
              class="text-xs px-3 py-1.5 font-semibold rounded bg-surface border border-border hover:bg-surface-subtle text-text-primary flex items-center gap-1.5 shadow-sm transition-all"
              title="Consultar buzones IMAP/Gmail para detectar respuestas automáticas"
            >
              ${getIcon('refreshCw', 'w-3.5 h-3.5 text-text-secondary')}
              <span>Sincronizar Respuestas</span>
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
          <table class="w-full text-left text-xs border-collapse">
            <thead>
              <tr class="border-b border-border text-text-secondary font-semibold uppercase tracking-wider text-[10px]">
                <th class="py-2.5 px-3 cursor-pointer hover:text-text-primary log-header-sort" data-col="contact">
                  Destinatario / Empresa ${logSortColumn === 'contact' ? (logSortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th class="py-2.5 px-3">Campaña / Asunto</th>
                <th class="py-2.5 px-3 cursor-pointer hover:text-text-primary log-header-sort" data-col="sent_at">
                  Fecha Envío ${logSortColumn === 'sent_at' ? (logSortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th class="py-2.5 px-3 cursor-pointer hover:text-text-primary log-header-sort" data-col="opens">
                  Aperturas ${logSortColumn === 'opens' ? (logSortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th class="py-2.5 px-3">Clicks en CTA</th>
                <th class="py-2.5 px-3">Respuesta</th>
                <th class="py-2.5 px-3">Estado</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-border/60">
              ${displayLogs.length === 0 ? `
                <tr>
                  <td colspan="7" class="py-8 text-center text-text-secondary italic">
                    No hay registros de envíos que coincidan con los filtros.
                  </td>
                </tr>
              ` : displayLogs.map(log => {
                const isOpened = log.opened_at !== null || log.open_count > 0;
                const isReplied = log.replied_at !== null;
                const clicksCount = log.clicks?.length || 0;
                const isInternalAlert = !log.contact_id || 
                  log.reply_channel === 'internal_notification' || 
                  (log.subject || '').startsWith('⚡ Nuevo Lead') || 
                  (log.subject || '').startsWith('⚡ Nuevo Diagnóstico');

                const contactDisplayName = isInternalAlert
                  ? `<span class="text-purple-700 font-semibold inline-flex items-center gap-1">${getIcon('bell', 'w-3 h-3 text-purple-600')} Aviso a Dirección</span>`
                  : `<span class="font-bold text-text-primary">${log.contact?.name || 'Contacto'}</span>`;

                const companyOrChannel = isInternalAlert
                  ? '<span class="text-purple-600 font-mono text-[10px]">Alerta Operador</span>'
                  : (log.client?.company && log.client.company !== '-' ? log.client.company : log.client?.name || '');

                const campaignDisplayName = isInternalAlert
                  ? 'Notificación Interna'
                  : (log.campaign?.name || 'Envío Directo / Webhook');

                return `
                  <tr class="hover:bg-surface-subtle/70 transition-colors ${isInternalAlert ? 'bg-purple-50/20' : ''}">
                    <td class="py-2.5 px-3">
                      <p>${contactDisplayName}</p>
                      <p class="text-[11px] text-text-secondary font-mono">${companyOrChannel}</p>
                    </td>
                    <td class="py-2.5 px-3 max-w-xs">
                      <p class="text-text-primary font-medium truncate">${log.subject}</p>
                      <p class="text-[10px] text-text-secondary font-mono truncate">${campaignDisplayName}</p>
                    </td>
                    <td class="py-2.5 px-3 font-mono text-[11px] text-text-secondary whitespace-nowrap">
                      ${new Date(log.sent_at).toLocaleDateString('es-AR')} ${new Date(log.sent_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td class="py-2.5 px-3 font-mono">
                      ${isOpened ? `
                        <span class="inline-flex items-center gap-1 text-emerald-600 font-bold">
                          ${getIcon('eye', 'w-3 h-3')}
                          ${log.open_count || 1}
                        </span>
                      ` : `
                        <span class="text-text-secondary/50 font-medium">0</span>
                      `}
                    </td>
                    <td class="py-2.5 px-3 font-mono">
                      ${clicksCount > 0 ? `
                        <span class="inline-flex items-center gap-1 text-blue-600 font-bold" title="${log.clicks.map(c => c.cta_text).join(', ')}">
                          ${getIcon('mousePointer', 'w-3 h-3')}
                          ${clicksCount}
                        </span>
                      ` : `
                        <span class="text-text-secondary/50">-</span>
                      `}
                    </td>
                    <td class="py-2.5 px-3">
                      ${isReplied ? `
                        <button type="button" class="btn-view-reply-modal inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-semibold font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-all cursor-pointer shadow-xs" data-log-id="${log.id}" title="${log.reply_notes ? escapeHtml(log.reply_notes.substring(0, 150)) : 'Ver detalle de la respuesta del prospecto'}">
                          ${getIcon('messageSquare', 'w-3 h-3 text-emerald-600')}
                          <span>Ver Respuesta</span>
                        </button>
                      ` : `
                        <span class="text-text-secondary/50 text-[11px]">Sin respuesta</span>
                      `}
                    </td>
                    <td class="py-2.5 px-3">
                      <span class="text-[10px] font-mono font-semibold uppercase px-2 py-0.5 rounded border ${
                        log.status === 'replied' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        log.status === 'clicked' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        log.status === 'opened' ? 'bg-zinc-100 text-zinc-700 border-zinc-300' :
                        'bg-surface-subtle text-text-secondary border-border'
                      }">
                        ${log.status}
                      </span>
                    </td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;
}

// -------------------------------------------------------------
// EVENT INITIALIZATION & MODAL HANDLERS
// -------------------------------------------------------------
export function initEmailMarketingEvents() {
  // Help Modal Button in Header
  const btnHelp = document.getElementById('btn-open-help-modal');
  if (btnHelp) {
    btnHelp.onclick = () => openHelpGuideModal();
  }

  // Tab switching
  document.querySelectorAll('.tab-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      activeTab = e.currentTarget.dataset.tab;
      document.querySelectorAll('.tab-btn').forEach(b => {
        b.className = 'tab-btn px-4 py-2.5 border-b-2 transition-colors border-transparent text-text-secondary hover:text-text-primary';
      });
      e.currentTarget.className = 'tab-btn px-4 py-2.5 border-b-2 transition-colors border-primary text-text-primary';
      const container = document.getElementById('email-marketing-tab-content');
      if (container) {
        container.innerHTML = renderActiveTabContent();
        bindCurrentTabEvents();
      }
    });
  });

  // Global batch runner
  const btnRunBatch = document.getElementById('btn-run-batch');
  if (btnRunBatch) {
    btnRunBatch.addEventListener('click', async () => {
      btnRunBatch.disabled = true;
      btnRunBatch.innerHTML = `<span>Procesando envíos...</span>`;
      try {
        const res = await DataService.runEmailAutomationBatch();
        showToast('Cola de envíos procesada con éxito.', 'success');
        setTimeout(() => window.location.reload(), 800);
      } catch (err) {
        showToast(`Envío procesado: ${err.message}`, 'info');
      } finally {
        btnRunBatch.disabled = false;
      }
    });
  }

  // Global sync replies
  const btnSyncReplies = document.getElementById('btn-sync-replies');
  if (btnSyncReplies) {
    const originalContent = btnSyncReplies.innerHTML;
    btnSyncReplies.addEventListener('click', async () => {
      btnSyncReplies.disabled = true;
      btnSyncReplies.innerHTML = `<span>Sincronizando...</span>`;
      try {
        const res = await DataService.checkEmailReplies();
        showToast('Sincronización completada. Respuestas verificadas.', 'success');
        setTimeout(() => window.location.reload(), 800);
      } catch (err) {
        showToast('Sincronización finalizada. Recuerda que puedes registrar respuestas directas con "Registrar respuesta".', 'info');
      } finally {
        btnSyncReplies.innerHTML = originalContent;
        btnSyncReplies.disabled = false;
      }
    });
  }

  bindCurrentTabEvents();
}

function bindCurrentTabEvents() {
  if (activeTab === 'campaigns') {
    bindCampaignsEvents();
  } else if (activeTab === 'templates') {
    bindTemplatesEvents();
  } else if (activeTab === 'accounts') {
    bindAccountsEvents();
  } else if (activeTab === 'stats') {
    bindStatsEvents();
  }
}

// -------------------------------------------------------------
// CAMPAIGN MODAL & CREATION
// -------------------------------------------------------------
function bindCampaignsEvents() {
  // Bind BOTH top and empty state buttons
  document.querySelectorAll('#btn-open-campaign-modal, #btn-empty-create-campaign').forEach(btn => {
    btn.onclick = () => openNewCampaignModal();
  });

  // Pause / Resume / Delete
  document.querySelectorAll('.btn-pause-campaign').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      await DataService.updateCampaign(id, { status: 'paused' });
      showToast('Campaña pausada.');
      window.location.reload();
    });
  });

  document.querySelectorAll('.btn-resume-campaign').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      await DataService.updateCampaign(id, { status: 'running' });
      showToast('Campaña reanudada.');
      window.location.reload();
    });
  });

  document.querySelectorAll('.btn-delete-campaign').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (confirm('¿Seguro que deseas eliminar esta campaña?')) {
        await DataService.deleteCampaign(btn.dataset.id);
        showToast('Campaña eliminada.');
        window.location.reload();
      }
    });
  });
}

function openNewCampaignModal() {
  const modalContainer = document.getElementById('marketing-modal-container');
  if (!modalContainer) return;

  // Filter options
  const stages = Array.from(new Set(cachedClients.map(c => c.phase).filter(Boolean)));
  const channels = Array.from(new Set(cachedClients.map(c => c.channel).filter(Boolean)));

  modalContainer.innerHTML = `
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-surface border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <!-- Modal Header -->
        <div class="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 class="font-bold text-sm text-text-primary">Nueva Campaña Comercial</h3>
            <p class="text-xs text-text-secondary">Configuración de destinatarios del CRM y secuencia de emails</p>
          </div>
          <button id="btn-close-campaign-modal" class="p-1 text-text-secondary hover:text-text-primary rounded">✕</button>
        </div>

        <!-- Modal Body -->
        <form id="form-create-campaign" class="p-5 overflow-y-auto space-y-4 text-xs">
          <!-- Step 1: Info -->
          <div class="space-y-3">
            <h4 class="font-bold text-text-primary uppercase tracking-wider text-[11px]">1. Datos de la Campaña</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block font-semibold text-text-primary mb-1">Nombre de Campaña</label>
                <input type="text" id="cmp-name" required placeholder="Ej: Reactivación Prospectos Fríos Q3" class="w-full p-2 bg-surface-subtle border border-border rounded outline-none" />
              </div>
              <div>
                <label class="block font-semibold text-text-primary mb-1">Cuenta de Envío de Google</label>
                <select id="cmp-account" required class="w-full p-2 bg-surface-subtle border border-border rounded outline-none font-medium">
                  ${cachedAccounts.length === 0 ? `<option value="">(No hay cuentas - conecta una en la pestaña Cuentas)</option>` : cachedAccounts.map(a => `<option value="${a.id}">${a.name} (${a.email_address})</option>`).join('')}
                </select>
              </div>
            </div>

            <div>
              <label class="block font-semibold text-text-primary mb-1">Objetivo Comercial</label>
              <input type="text" id="cmp-objective" placeholder="Ej: Agendar diagnóstico breve de 15 minutos" class="w-full p-2 bg-surface-subtle border border-border rounded outline-none" />
            </div>
          </div>

          <!-- Step 2: CRM Filters & Live Recipient Preview -->
          <div class="space-y-3 pt-3 border-t border-border">
            <h4 class="font-bold text-text-primary uppercase tracking-wider text-[11px]">2. Segmentación del CRM</h4>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <label class="block font-medium text-text-secondary mb-1">Etapa Comercial</label>
                <select id="filter-cmp-stage" class="w-full p-1.5 bg-surface-subtle border border-border rounded outline-none">
                  <option value="all">Todas las etapas</option>
                  ${stages.map(s => `<option value="${s}">${s}</option>`).join('')}
                </select>
              </div>
              <div>
                <label class="block font-medium text-text-secondary mb-1">Canal de Origen</label>
                <select id="filter-cmp-channel" class="w-full p-1.5 bg-surface-subtle border border-border rounded outline-none">
                  <option value="all">Todos los canales</option>
                  ${channels.map(ch => `<option value="${ch}">${ch}</option>`).join('')}
                </select>
              </div>
              <div>
                <label class="block font-medium text-text-secondary mb-1">Destinatarios</label>
                <select id="filter-cmp-contacts-scope" class="w-full p-1.5 bg-surface-subtle border border-border rounded outline-none font-semibold">
                  <option value="primary">Solo contacto principal (Recomendado)</option>
                  <option value="all">Todos los contactos válidos</option>
                </select>
              </div>
            </div>

            <!-- Recipient Preview Box -->
            <div class="space-y-1.5">
              <div class="flex items-center justify-between text-[11px]">
                <span class="font-semibold text-text-primary" id="matching-recipients-count">Cargando destinatarios...</span>
                <span class="text-text-secondary">Desmarca para excluir individualmente</span>
              </div>
              <div id="campaign-recipient-preview-list" class="max-h-36 overflow-y-auto p-2 bg-surface-subtle rounded border border-border divide-y divide-border/60">
                <!-- Injected dynamically -->
              </div>
            </div>
          </div>

          <!-- Step 3: Sequence & Template -->
          <div class="space-y-3 pt-3 border-t border-border">
            <h4 class="font-bold text-text-primary uppercase tracking-wider text-[11px]">3. Secuencia de Envíos</h4>
            <div class="space-y-2">
              <div class="p-2.5 rounded bg-surface-subtle border border-border flex items-center justify-between gap-3">
                <span class="font-semibold text-text-primary w-24">Día 0 (Inicial):</span>
                <select id="seq-step-0" required class="flex-1 p-1.5 bg-surface border border-border rounded outline-none">
                  ${cachedTemplates.map(t => `<option value="${t.id}">${t.name}</option>`).join('')}
                </select>
              </div>
              <div class="p-2.5 rounded bg-surface-subtle border border-border flex items-center justify-between gap-3">
                <span class="font-semibold text-text-primary w-24">Día 15 (Sin rta):</span>
                <select id="seq-step-1" class="flex-1 p-1.5 bg-surface border border-border rounded outline-none">
                  <option value="">-- No enviar segundo paso --</option>
                  ${cachedTemplates.map(t => `<option value="${t.id}">${t.name}</option>`).join('')}
                </select>
              </div>
              <div class="p-2.5 rounded bg-surface-subtle border border-border flex items-center justify-between gap-3">
                <span class="font-semibold text-text-primary w-24">Día 30 (Sin rta):</span>
                <select id="seq-step-2" class="flex-1 p-1.5 bg-surface border border-border rounded outline-none">
                  <option value="">-- No enviar tercer paso --</option>
                  ${cachedTemplates.map(t => `<option value="${t.id}">${t.name}</option>`).join('')}
                </select>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="flex items-center justify-end gap-2 pt-4 border-t border-border">
            <button type="button" id="btn-cancel-campaign-modal" class="px-3.5 py-2 text-xs font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded bg-primary text-white hover:bg-primary-hover shadow-sm">
              Lanzar Campaña
            </button>
          </div>
        </form>
      </div>
    </div>
  `;

  // Close handlers
  const close = () => { modalContainer.innerHTML = ''; };
  document.getElementById('btn-close-campaign-modal')?.addEventListener('click', close);
  document.getElementById('btn-cancel-campaign-modal')?.addEventListener('click', close);

  // Recipient preview updater
  const updateRecipients = () => {
    const stageVal = document.getElementById('filter-cmp-stage')?.value;
    const channelVal = document.getElementById('filter-cmp-channel')?.value;
    const scopeVal = document.getElementById('filter-cmp-contacts-scope')?.value;

    let matchingClients = cachedClients.filter(c => {
      if (c.do_not_contact) return false;
      if (stageVal !== 'all' && c.phase !== stageVal) return false;
      if (channelVal !== 'all' && c.channel !== channelVal) return false;
      return true;
    });

    const clientIds = new Set(matchingClients.map(c => c.id));
    let matchingContacts = cachedContacts.filter(cnt => {
      if (cnt.do_not_contact || !cnt.email) return false;
      if (!clientIds.has(cnt.client_id)) return false;
      if (scopeVal === 'primary' && !cnt.is_primary) return false;
      return true;
    });

    const countEl = document.getElementById('matching-recipients-count');
    if (countEl) countEl.innerText = `${matchingContacts.length} contactos seleccionados`;

    const listEl = document.getElementById('campaign-recipient-preview-list');
    if (listEl) {
      if (matchingContacts.length === 0) {
        listEl.innerHTML = `<p class="text-text-secondary italic p-2 text-center">No hay contactos con email válido que cumplan estos filtros.</p>`;
      } else {
        listEl.innerHTML = matchingContacts.map(cnt => {
          const client = cachedClients.find(c => c.id === cnt.client_id);
          return `
            <label class="flex items-center justify-between p-1.5 hover:bg-surface cursor-pointer rounded">
              <div class="flex items-center gap-2">
                <input type="checkbox" name="recipient_contact" value="${cnt.id}" checked class="rounded border-border text-primary focus:ring-primary" />
                <span class="font-medium text-text-primary">${cnt.name}</span>
                <span class="text-text-secondary text-[11px] font-mono">(${cnt.email})</span>
              </div>
              <span class="text-[10px] font-mono text-text-secondary">${client?.company && client.company !== '-' ? client.company : client?.name || ''}</span>
            </label>
          `;
        }).join('');
      }
    }
  };

  document.getElementById('filter-cmp-stage')?.addEventListener('change', updateRecipients);
  document.getElementById('filter-cmp-channel')?.addEventListener('change', updateRecipients);
  document.getElementById('filter-cmp-contacts-scope')?.addEventListener('change', updateRecipients);
  updateRecipients();

  // Form submission
  const form = document.getElementById('form-create-campaign');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = document.getElementById('cmp-name').value;
    const accountId = Number(document.getElementById('cmp-account').value);
    const objective = document.getElementById('cmp-objective').value;
    const step0Tpl = Number(document.getElementById('seq-step-0').value);
    const step1Tpl = document.getElementById('seq-step-1').value ? Number(document.getElementById('seq-step-1').value) : null;
    const step2Tpl = document.getElementById('seq-step-2').value ? Number(document.getElementById('seq-step-2').value) : null;

    if (!accountId) {
      alert('Debes seleccionar o conectar una cuenta de envío de Google primero.');
      return;
    }

    const checkedContactIds = Array.from(document.querySelectorAll('input[name="recipient_contact"]:checked')).map(cb => Number(cb.value));

    if (checkedContactIds.length === 0) {
      alert('Debes seleccionar al menos un contacto destinatario.');
      return;
    }

    const steps = [
      { step_order: 0, delay_days: 0, template_id: step0Tpl, condition: 'always' }
    ];
    if (step1Tpl) steps.push({ step_order: 1, delay_days: 15, template_id: step1Tpl, condition: 'if_no_reply' });
    if (step2Tpl) steps.push({ step_order: 2, delay_days: 30, template_id: step2Tpl, condition: 'if_no_reply' });

    try {
      await DataService.createCampaign({
        campaign: {
          name,
          objective,
          account_id: accountId,
          template_id: step0Tpl,
          status: 'running',
          start_at: new Date().toISOString()
        },
        steps,
        recipientContactIds: checkedContactIds
      });

      showToast('¡Campaña creada y lanzada con éxito!');
      close();
      window.location.reload();
    } catch (err) {
      alert(`Error creando campaña: ${err.message}`);
    }
  });
}

// -------------------------------------------------------------
// TEMPLATES EDITOR HANDLERS
// -------------------------------------------------------------
function bindTemplatesEvents() {
  const form = document.getElementById('form-template-editor');
  const previewBox = document.getElementById('tpl-preview-box');
  const bodyTextarea = document.getElementById('tpl-body');
  const subjectInput = document.getElementById('tpl-subject');
  const ctaToggleBtn = document.getElementById('btn-toggle-cta');
  const ctaInputs = document.getElementById('cta-inputs-container');
  const btnNewTemplate = document.getElementById('btn-new-template');

  // New Template button handler
  if (btnNewTemplate) {
    btnNewTemplate.addEventListener('click', () => {
      if (form) form.reset();
      document.getElementById('tpl-id').value = '';
      document.getElementById('editor-title').innerText = 'Nueva Plantilla';
      ctaInputs?.classList.add('hidden');
      if (previewBox) {
        previewBox.innerHTML = `<p class="text-text-secondary italic">Escribe en el editor para previsualizar...</p>`;
      }
      document.getElementById('tpl-name')?.focus();
      document.getElementById('template-editor-container')?.scrollIntoView({ behavior: 'smooth' });
      showToast('Formulario de plantilla listo para redactar.', 'info');
    });
  }

  if (ctaToggleBtn && ctaInputs) {
    ctaToggleBtn.addEventListener('click', () => {
      ctaInputs.classList.toggle('hidden');
    });
  }

  // Helper para generar el HTML del botón con diseño editorial Alerce (DESIGN.md)
  const getCtaButtonHtml = (text, url, style = 'acid') => {
    let bg = '#D2F26B';
    let color = '#0D1A14';
    let border = '1px solid #B9DC45';
    let arrowBg = '#0D1A14';
    let arrowColor = '#D2F26B';

    if (style === 'forest') {
      bg = '#0F3B2C';
      color = '#F3F0E8';
      border = '1px solid #0F3B2C';
      arrowBg = '#D2F26B';
      arrowColor = '#0D1A14';
    } else if (style === 'ink') {
      bg = '#0D1A14';
      color = '#F3F0E8';
      border = '1px solid #0D1A14';
      arrowBg = '#D2F26B';
      arrowColor = '#0D1A14';
    } else if (style === 'outline') {
      bg = 'transparent';
      color = '#0D1A14';
      border = '1px solid rgba(13,26,20,0.3)';
      arrowBg = '#0D1A14';
      arrowColor = '#F3F0E8';
    }

    return `
      <div class="py-2.5 my-1" data-cta-block="true">
        <a href="${url || '#'}" target="_blank" style="display:inline-flex;align-items:center;gap:8px;padding:8px 18px;border-radius:9999px;background-color:${bg};color:${color};border:${border};font-family:system-ui,-apple-system,sans-serif;font-size:12px;font-weight:700;text-decoration:none;box-shadow:0 1px 3px rgba(0,0,0,0.08);cursor:pointer;pointer-events:none;">
          <span>${text || 'Agendar diagnóstico'}</span>
          <span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:9999px;background-color:${arrowBg};color:${arrowColor};font-size:10px;line-height:1;">➔</span>
        </a>
      </div>
    `;
  };

  // Live preview updater
  const updatePreview = () => {
    const rawBody = bodyTextarea?.value || '';
    const rawSubject = subjectInput?.value || '';
    const ctaText = document.getElementById('cta-text')?.value || '';
    const ctaUrl = document.getElementById('cta-url')?.value || '';
    const ctaStyle = document.getElementById('cta-style')?.value || 'acid';

    let content = rawBody
      .replace(/\{\{contact\.first_name\}\}/gi, 'Juan')
      .replace(/\{\{contact\.name\}\}/gi, 'Juan Pérez')
      .replace(/\{\{company\.name\}\}/gi, 'Acme Corp')
      .replace(/\{\{sender\.name\}\}/gi, 'Martín Batarev');

    let replacedCtaInPreview = false;
    const buttonHtml = getCtaButtonHtml(ctaText, ctaUrl, ctaStyle);

    // 1. Reemplazo del tag explícito {{cta}} en cualquier parte del cuerpo
    if (content.includes('{{cta}}')) {
      content = content.replace(/\{\{cta\}\}/gi, buttonHtml);
      replacedCtaInPreview = true;
    }

    // 2. Reemplazo de tags por variante ej: {{cta:acid}}, {{cta:forest}}, {{cta:ink}}
    ['acid', 'forest', 'ink', 'outline'].forEach(st => {
      const rx = new RegExp(`\\{\\{cta:${st}\\}\\}`, 'gi');
      if (rx.test(content)) {
        content = content.replace(rx, getCtaButtonHtml(ctaText, ctaUrl, st));
        replacedCtaInPreview = true;
      }
    });

    // 3. Reemplazo por corchetes e.g. [Texto del Botón]
    if (ctaText && ctaUrl && !replacedCtaInPreview) {
      const escapedText = ctaText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const exactRegex = new RegExp(`\\[${escapedText}\\]`, 'gi');
      if (exactRegex.test(content)) {
        content = content.replace(exactRegex, buttonHtml);
        replacedCtaInPreview = true;
      } else {
        const genericBracketMatch = content.match(/\[([A-ZÁÉÍÓÚÑa-záéíóúñ\s0-9\-_:?¿!¡]+)\]/);
        if (genericBracketMatch) {
          content = content.replace(genericBracketMatch[0], buttonHtml);
          replacedCtaInPreview = true;
        }
      }
    }

    const isHtml = /<(?:p|div|table|h[1-6]|ul|ol|blockquote|section|article|header|footer)[\s>]/i.test(content);
    let finalHtml = '';
    if (isHtml) {
      finalHtml = content;
    } else {
      finalHtml = content.split('\n\n').map(p => {
        const trimmed = p.trim();
        if (!trimmed) return '';
        if (trimmed.includes('data-cta-block="true"')) return trimmed;
        return `<p class="mb-2 leading-relaxed">${trimmed.replace(/\n/g, '<br/>')}</p>`;
      }).filter(Boolean).join('');
    }

    // Si hay CTA configurado pero no se ubicó con {{cta}} ni corchetes, agregar al final
    if (ctaText && ctaUrl && !replacedCtaInPreview) {
      finalHtml += buttonHtml;
    }

    if (previewBox) {
      previewBox.innerHTML = `
        <div class="pb-2 border-b border-border/60 text-[11px] font-mono text-text-secondary">
          <strong>Asunto:</strong> ${rawSubject.replace(/\{\{contact\.first_name\}\}/gi, 'Juan').replace(/\{\{company\.name\}\}/gi, 'Acme Corp')}
        </div>
        <div class="pt-2 text-text-primary">${finalHtml || '<span class="text-text-secondary italic">Sin contenido aún...</span>'}</div>
      `;
    }
  };

  bodyTextarea?.addEventListener('input', updatePreview);
  subjectInput?.addEventListener('input', updatePreview);
  document.getElementById('cta-text')?.addEventListener('input', updatePreview);
  document.getElementById('cta-url')?.addEventListener('input', updatePreview);
  document.getElementById('cta-style')?.addEventListener('change', updatePreview);

  // Inserción de tag {{cta}} en la posición actual del cursor
  const insertCtaTagAtCursor = () => {
    if (!bodyTextarea) return;
    const start = bodyTextarea.selectionStart;
    const end = bodyTextarea.selectionEnd;
    const currentVal = bodyTextarea.value;
    const tag = '\n\n{{cta}}\n\n';
    bodyTextarea.value = currentVal.substring(0, start) + tag + currentVal.substring(end);
    const nextPos = start + tag.length;
    bodyTextarea.focus();
    bodyTextarea.setSelectionRange(nextPos, nextPos);
    if (ctaInputs?.classList.contains('hidden')) {
      ctaInputs.classList.remove('hidden');
    }
    updatePreview();
  };

  document.getElementById('btn-insert-cta-tag')?.addEventListener('click', insertCtaTagAtCursor);
  document.getElementById('btn-copy-cta-tag')?.addEventListener('click', insertCtaTagAtCursor);

  // Insert variable pill into active input
  document.querySelectorAll('.btn-insert-var').forEach(btn => {
    btn.addEventListener('click', () => {
      const varTag = btn.dataset.var;
      if (bodyTextarea) {
        bodyTextarea.value += varTag;
        updatePreview();
      }
    });
  });

  // Insert HTML tags or formatting
  document.querySelectorAll('.btn-insert-html').forEach(btn => {
    btn.addEventListener('click', () => {
      const openTag = btn.dataset.open || '';
      const closeTag = btn.dataset.close || '';
      if (!bodyTextarea) return;

      const start = bodyTextarea.selectionStart;
      const end = bodyTextarea.selectionEnd;
      const currentVal = bodyTextarea.value;
      const selectedText = currentVal.substring(start, end);

      const replacement = openTag + selectedText + closeTag;
      bodyTextarea.value = currentVal.substring(0, start) + replacement + currentVal.substring(end);
      
      const newCursorPos = start + openTag.length + selectedText.length;
      bodyTextarea.focus();
      bodyTextarea.setSelectionRange(newCursorPos, newCursorPos);
      updatePreview();
    });
  });

  // Load existing template into editor
  document.querySelectorAll('.template-item').forEach(item => {
    item.addEventListener('click', () => {
      const id = Number(item.dataset.id);
      const t = cachedTemplates.find(tpl => tpl.id === id);
      if (!t) return;

      document.getElementById('tpl-id').value = t.id;
      document.getElementById('tpl-name').value = t.name;
      document.getElementById('tpl-subject').value = t.subject;
      document.getElementById('tpl-body').value = t.body_markdown || t.body_html || '';

      const ctas = Array.isArray(t.ctas) && t.ctas.length > 0 ? t.ctas[0] : null;
      if (ctas) {
        ctaInputs?.classList.remove('hidden');
        document.getElementById('cta-text').value = ctas.text || '';
        document.getElementById('cta-url').value = ctas.url || '';
        if (document.getElementById('cta-style')) {
          document.getElementById('cta-style').value = ctas.style || 'acid';
        }
      } else {
        ctaInputs?.classList.add('hidden');
        document.getElementById('cta-text').value = '';
        document.getElementById('cta-url').value = '';
      }
      document.getElementById('editor-title').innerText = `Editando: ${t.name}`;
      updatePreview();
      document.getElementById('template-editor-container')?.scrollIntoView({ behavior: 'smooth' });
    });
  });

  // Reset form
  document.getElementById('btn-reset-template-form')?.addEventListener('click', () => {
    if (form) form.reset();
    document.getElementById('tpl-id').value = '';
    document.getElementById('editor-title').innerText = 'Nueva Plantilla';
    ctaInputs?.classList.add('hidden');
    updatePreview();
  });

  // Save template
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const id = document.getElementById('tpl-id').value;
    const name = document.getElementById('tpl-name').value;
    const subject = document.getElementById('tpl-subject').value;
    const body = document.getElementById('tpl-body').value;
    const ctaText = document.getElementById('cta-text')?.value;
    const ctaUrl = document.getElementById('cta-url')?.value;
    const ctaStyle = document.getElementById('cta-style')?.value || 'acid';

    const ctas = ctaText && ctaUrl ? [{ id: 'cta_1', text: ctaText, url: ctaUrl, style: ctaStyle }] : [];

    try {
      if (id) {
        await DataService.updateEmailTemplate(Number(id), {
          name, subject, body_markdown: body, body_html: body, ctas
        });
        showToast('Plantilla actualizada.');
      } else {
        await DataService.createEmailTemplate({
          name, subject, body_markdown: body, body_html: body, ctas
        });
        showToast('Plantilla creada.');
      }
      window.location.reload();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  });

  // Delete template
  document.querySelectorAll('.btn-delete-template').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (confirm('¿Eliminar esta plantilla?')) {
        await DataService.deleteEmailTemplate(Number(btn.dataset.id));
        showToast('Plantilla eliminada.');
        window.location.reload();
      }
    });
  });

  // Full Preview button in Editor
  const btnFullPreview = document.getElementById('btn-full-preview');
  if (btnFullPreview) {
    btnFullPreview.addEventListener('click', () => {
      const name = document.getElementById('tpl-name')?.value || 'Vista Previa de Plantilla';
      const subject = document.getElementById('tpl-subject')?.value || 'Consulta comercial';
      const body = document.getElementById('tpl-body')?.value || '';
      const ctaText = document.getElementById('cta-text')?.value || '';
      const ctaUrl = document.getElementById('cta-url')?.value || '';
      const ctaStyle = document.getElementById('cta-style')?.value || 'forest';

      openFullTemplatePreviewModal({
        name,
        subject,
        body,
        ctaText,
        ctaUrl,
        ctaStyle,
        ctas: ctaText && ctaUrl ? [{ id: 'cta_1', text: ctaText, url: ctaUrl, style: ctaStyle }] : []
      });
    });
  }

  // Card Preview buttons
  document.querySelectorAll('.btn-preview-card-template').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = Number(btn.dataset.id);
      const t = cachedTemplates.find(tpl => tpl.id === id);
      if (!t) return;
      const ctas = Array.isArray(t.ctas) && t.ctas.length > 0 ? t.ctas[0] : null;
      openFullTemplatePreviewModal({
        name: t.name,
        subject: t.subject,
        body: t.body_markdown || t.body_html || '',
        ctaText: ctas?.text || '',
        ctaUrl: ctas?.url || '',
        ctaStyle: ctas?.style || 'forest',
        ctas: Array.isArray(t.ctas) ? t.ctas : []
      });
    });
  });

  // Insert resource button
  const btnInsertResource = document.getElementById('btn-insert-resource-modal');
  if (btnInsertResource) {
    btnInsertResource.addEventListener('click', () => {
      openInsertResourceModal();
    });
  }
}

// -------------------------------------------------------------
// ACCOUNTS HANDLERS & MODAL
// -------------------------------------------------------------
function bindAccountsEvents() {
  // Bind BOTH top header button and center empty-state button
  document.querySelectorAll('#btn-open-account-modal, #btn-empty-create-account').forEach(btn => {
    btn.onclick = () => openNewAccountModal();
  });

  // Edit / configure account
  document.querySelectorAll('.btn-edit-account').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = Number(btn.dataset.id);
      const acc = cachedAccounts.find(a => a.id === id);
      if (acc) openNewAccountModal(acc);
    });
  });

  // Toggle pause
  document.querySelectorAll('.btn-toggle-account-pause').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = Number(btn.dataset.id);
      const current = btn.dataset.status;
      const newStatus = current === 'paused' ? 'active' : 'paused';
      await DataService.updateEmailAccount(id, { status: newStatus });
      showToast(newStatus === 'paused' ? 'Cuenta pausada.' : 'Cuenta reactivada.');
      window.location.reload();
    });
  });

  // Delete account
  document.querySelectorAll('.btn-delete-account').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (confirm('¿Desconectar esta cuenta?')) {
        await DataService.deleteEmailAccount(Number(btn.dataset.id));
        showToast('Cuenta desconectada.');
        window.location.reload();
      }
    });
  });
}

function openNewAccountModal(existingAccount = null) {
  const modalContainer = document.getElementById('marketing-modal-container');
  if (!modalContainer) return;

  const isEditing = Boolean(existingAccount);
  const defaultEmail = existingAccount?.email_address || '';
  const isGmailAddress = defaultEmail.toLowerCase().endsWith('@gmail.com');
  // If editing an existing @gmail.com account or creating a @gmail.com account, default to app_password
  const initialAuthType = existingAccount 
    ? (isGmailAddress && existingAccount.auth_type === 'service_account' ? 'app_password' : existingAccount.auth_type)
    : 'app_password';

  modalContainer.innerHTML = `
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-surface border border-border rounded-lg shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div class="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 class="font-bold text-sm text-text-primary">${isEditing ? 'Configurar Cuenta de Envío' : 'Conectar Cuenta de Envío de Google'}</h3>
            <p class="text-[11px] text-text-secondary">${isEditing ? `Modificando credenciales de ${existingAccount.email_address}` : 'Soporte para Gmail Personal y Google Workspace'}</p>
          </div>
          <button id="btn-close-account-modal" class="p-1 text-text-secondary hover:text-text-primary rounded">✕</button>
        </div>

        <form id="form-connect-account" class="p-5 space-y-4 text-xs">
          <div>
            <label class="block font-semibold text-text-primary mb-1">Nombre descriptivo de la cuenta</label>
            <input type="text" id="acc-name" required value="${existingAccount?.name ? existingAccount.name.replace(/"/g, '&quot;') : ''}" placeholder="Ej: Martín Personal o Equipo Alerce Oficial" class="w-full p-2 bg-surface-subtle border border-border rounded outline-none" />
          </div>

          <div>
            <label class="block font-semibold text-text-primary mb-1">Dirección de correo electrónico remitente</label>
            <input type="email" id="acc-email" required value="${defaultEmail ? defaultEmail.replace(/"/g, '&quot;') : ''}" placeholder="tuemail@gmail.com o contacto@equipoalerce.com" class="w-full p-2 bg-surface-subtle border border-border rounded outline-none font-mono" />
          </div>

          <div>
            <label class="block font-semibold text-text-primary mb-1">Método de Integración</label>
            <select id="acc-type" class="w-full p-2 bg-surface-subtle border border-border rounded outline-none font-medium">
              <option value="app_password" ${initialAuthType === 'app_password' ? 'selected' : ''}>Contraseña de Aplicación de Google (Recomendado para @gmail.com personal)</option>
              <option value="service_account" ${initialAuthType === 'service_account' ? 'selected' : ''}>Google Service Account (JSON - Exclusivo para Google Workspace)</option>
            </select>
          </div>

          <!-- Dynamic Gmail Notice -->
          <div id="gmail-recommendation-box" class="p-3 bg-amber-50/90 border border-amber-200 rounded-md text-amber-900 text-[11px] space-y-1.5 ${isGmailAddress ? '' : 'hidden'}">
            <p class="font-bold flex items-center gap-1.5 text-amber-950">
              <span>⚠️ Requisito para casillas @gmail.com personales:</span>
            </p>
            <p class="text-[10.5px] leading-relaxed">
              Google <strong>no permite Service Accounts</strong> para casillas personales <code>@gmail.com</code> (genera error <code>unauthorized_client</code>). 
              Debes usar <strong>Contraseña de Aplicación</strong> de 16 caracteres:
            </p>
            <ol class="list-decimal list-inside text-[10px] space-y-0.5 text-amber-950">
              <li>Entra a <a href="https://myaccount.google.com/apppasswords" target="_blank" class="underline font-bold text-primary">myaccount.google.com/apppasswords</a> (o Google > Seguridad > Verificación en 2 pasos > Contraseñas de aplicaciones).</li>
              <li>Genera una contraseña con el nombre <code>Business OS</code>.</li>
              <li>Copia las 16 letras y pégalas abajo.</li>
            </ol>
          </div>

          <!-- Credentials Input -->
          <div id="acc-creds-container" class="space-y-1">
            <div class="flex items-center justify-between">
              <label class="block font-semibold text-text-primary" id="lbl-credentials-title">
                ${initialAuthType === 'app_password' ? 'Contraseña de Aplicación de 16 letras' : 'Clave JSON de la Service Account'}
              </label>
              <span class="text-[10px] text-text-secondary" id="lbl-credentials-source">
                ${initialAuthType === 'app_password' ? 'Google Account Security' : 'Google Cloud Console'}
              </span>
            </div>
            <p class="text-[11px] text-text-secondary" id="lbl-credentials-help">
              ${initialAuthType === 'app_password' 
                ? 'Generada en tu Cuenta de Google > Seguridad > Verificación en 2 pasos > Contraseñas de aplicaciones.'
                : 'Pega el contenido del archivo JSON generado en Google Cloud Console. Requiere delegación de dominio en Google Workspace.'}
            </p>
            <textarea id="acc-json" rows="${initialAuthType === 'app_password' ? '2' : '6'}" 
              placeholder="${initialAuthType === 'app_password' ? 'ej: abcd efgh ijkl mnop (puedes pegarla con o sin espacios)' : '{ &quot;type&quot;: &quot;service_account&quot;, &quot;project_id&quot;: &quot;...&quot;, &quot;private_key&quot;: &quot;-----BEGIN PRIVATE KEY-----...&quot; }'}" 
              class="w-full p-2 bg-surface-subtle border border-border rounded font-mono text-[11px] outline-none"></textarea>
            ${isEditing ? `<p class="text-[10px] text-text-secondary italic">Si dejas este campo en blanco, se conservará la contraseña/clave que ya tenías guardada.</p>` : ''}
          </div>

          <div>
            <label class="block font-semibold text-text-primary mb-1">Límite Diario de Envíos</label>
            <p class="text-[11px] text-text-secondary mb-1">El sistema pausará automáticamente los envíos al alcanzar este límite para proteger la reputación del buzón.</p>
            <input type="number" id="acc-limit" value="${existingAccount?.daily_limit || 100}" min="1" max="1500" class="w-28 p-2 bg-surface-subtle border border-border rounded outline-none font-mono" />
          </div>

          <div class="flex items-center justify-end gap-2 pt-3 border-t border-border">
            <button type="button" id="btn-cancel-account-modal" class="px-3.5 py-2 text-xs font-medium border border-border rounded bg-surface hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded bg-primary text-white hover:bg-primary-hover shadow-sm">
              ${isEditing ? 'Guardar Cambios' : 'Guardar Cuenta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  `;

  const close = () => { modalContainer.innerHTML = ''; };
  document.getElementById('btn-close-account-modal')?.addEventListener('click', close);
  document.getElementById('btn-cancel-account-modal')?.addEventListener('click', close);

  const accTypeSelect = document.getElementById('acc-type');
  const accEmailInput = document.getElementById('acc-email');
  const lblTitle = document.getElementById('lbl-credentials-title');
  const lblSource = document.getElementById('lbl-credentials-source');
  const lblHelp = document.getElementById('lbl-credentials-help');
  const txtJson = document.getElementById('acc-json');
  const gmailBox = document.getElementById('gmail-recommendation-box');

  const updateTypeUI = (type) => {
    if (type === 'app_password') {
      if (lblTitle) lblTitle.innerText = 'Contraseña de Aplicación (16 letras)';
      if (lblSource) lblSource.innerText = 'Google Account Security';
      if (lblHelp) lblHelp.innerHTML = 'Generada en tu Cuenta de Google > Seguridad > Verificación en 2 pasos > <a href="https://myaccount.google.com/apppasswords" target="_blank" class="underline font-bold text-primary">Contraseñas de aplicaciones</a>.';
      if (txtJson) {
        txtJson.rows = 2;
        txtJson.placeholder = 'ej: abcd efgh ijkl mnop (puedes pegarla con o sin espacios)';
      }
    } else {
      if (lblTitle) lblTitle.innerText = 'Clave JSON de la Service Account';
      if (lblSource) lblSource.innerText = 'Google Cloud Console';
      if (lblHelp) lblHelp.innerText = 'Pega el archivo JSON de la Service Account. Nota: Solo funciona para dominios de Google Workspace con Delegación de Dominio configurada.';
      if (txtJson) {
        txtJson.rows = 6;
        txtJson.placeholder = '{ "type": "service_account", "project_id": "...", "private_key": "-----BEGIN PRIVATE KEY-----...", "client_email": "..." }';
      }
    }
  };

  accTypeSelect?.addEventListener('change', () => {
    updateTypeUI(accTypeSelect.value);
    const emailVal = accEmailInput?.value.trim().toLowerCase() || '';
    if (emailVal.endsWith('@gmail.com') && accTypeSelect.value === 'service_account') {
      gmailBox?.classList.remove('hidden');
    } else if (!emailVal.endsWith('@gmail.com')) {
      gmailBox?.classList.add('hidden');
    }
  });

  accEmailInput?.addEventListener('input', () => {
    const emailVal = accEmailInput.value.trim().toLowerCase();
    if (emailVal.endsWith('@gmail.com')) {
      gmailBox?.classList.remove('hidden');
      if (accTypeSelect.value === 'service_account') {
        accTypeSelect.value = 'app_password';
        updateTypeUI('app_password');
      }
    } else {
      gmailBox?.classList.add('hidden');
    }
  });

  const form = document.getElementById('form-connect-account');
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = document.getElementById('acc-name').value.trim();
    const email = document.getElementById('acc-email').value.trim();
    const type = document.getElementById('acc-type').value;
    const limit = Number(document.getElementById('acc-limit').value || 100);
    const rawCreds = document.getElementById('acc-json').value.trim();

    let credentials = null;
    if (rawCreds) {
      if (type === 'service_account') {
        try {
          credentials = JSON.parse(rawCreds);
        } catch (_) {
          alert('El JSON de la Service Account no tiene un formato válido.');
          return;
        }
      } else {
        // App password: strip any whitespace
        credentials = { app_password: rawCreds.replace(/\s+/g, '') };
      }
    } else if (!isEditing) {
      alert(type === 'app_password' 
        ? 'Por favor ingresa la Contraseña de Aplicación de 16 letras.' 
        : 'Por favor ingresa la clave JSON de la Service Account.');
      return;
    }

    try {
      if (isEditing) {
        const updates = {
          name,
          email_address: email,
          auth_type: type,
          daily_limit: limit
        };
        if (credentials) {
          updates.credentials = credentials;
        }
        await DataService.updateEmailAccount(existingAccount.id, updates);
        showToast('¡Cuenta actualizada con éxito!');
      } else {
        await DataService.createEmailAccount({
          name,
          email_address: email,
          auth_type: type,
          daily_limit: limit,
          credentials: credentials || {},
          status: 'active'
        });
        showToast('¡Cuenta conectada con éxito!');
      }
      close();
      window.location.reload();
    } catch (err) {
      alert(`Error al guardar cuenta: ${err.message}`);
    }
  });
}

// -------------------------------------------------------------
// STATS TAB HANDLERS (Sorting & Filter for Individual Logs Table)
// -------------------------------------------------------------
function bindStatsEvents() {
  const selectStatus = document.getElementById('select-log-status');
  if (selectStatus) {
    selectStatus.addEventListener('change', (e) => {
      logStatusFilter = e.target.value;
      refreshStatsView();
    });
  }

  const inputSearch = document.getElementById('input-log-search');
  if (inputSearch) {
    inputSearch.addEventListener('input', (e) => {
      logSearchTerm = e.target.value;
      refreshStatsView();
    });
  }

  document.querySelectorAll('.log-header-sort').forEach(th => {
    th.addEventListener('click', () => {
      const col = th.dataset.col;
      if (logSortColumn === col) {
        logSortDir = logSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        logSortColumn = col;
        logSortDir = 'desc';
      }
      refreshStatsView();
    });
  });

  const btnSyncTable = document.getElementById('btn-sync-replies-table');
  if (btnSyncTable) {
    btnSyncTable.addEventListener('click', async () => {
      btnSyncTable.disabled = true;
      btnSyncTable.innerHTML = `<span class="animate-spin inline-block mr-1">⟳</span> Sincronizando...`;
      try {
        const res = await DataService.checkEmailReplies();
        const count = res?.replies_detected || 0;
        if (count > 0) {
          showToast(`¡Sincronización exitosa! ${count} respuesta(s) detectada(s).`, 'success');
        } else {
          showToast('Buzones sincronizados. No se detectaron nuevas respuestas.', 'info');
        }
        const [freshStats, freshLogs] = await Promise.all([
          DataService.getEmailMarketingStats().catch(() => null),
          DataService.getEmailLogs().catch(() => [])
        ]);
        if (freshStats) cachedStats = freshStats;
        if (freshLogs) cachedLogs = freshLogs;
        refreshStatsView();
      } catch (err) {
        showToast(`Error al sincronizar buzones: ${err.message}`, 'error');
      } finally {
        btnSyncTable.disabled = false;
        btnSyncTable.innerHTML = `${getIcon('refreshCw', 'w-3.5 h-3.5 text-text-secondary')} <span>Sincronizar Respuestas</span>`;
      }
    });
  }

  // Bind view reply modal buttons
  document.querySelectorAll('.btn-view-reply-modal').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      openReplyDetailModal(btn.dataset.logId);
    });
  });
}

function refreshStatsView() {
  const container = document.getElementById('email-marketing-tab-content');
  if (container) {
    container.innerHTML = renderStatsTab();
    bindStatsEvents();
  }
}

// -------------------------------------------------------------
// MODAL DE DETALLE DE RESPUESTA DEL PROSPECTO
// -------------------------------------------------------------
function openReplyDetailModal(logId) {
  const modalContainer = document.getElementById('marketing-modal-container');
  if (!modalContainer) return;

  const log = cachedLogs.find(l => String(l.id) === String(logId));
  if (!log) {
    showToast('No se encontró el registro de envío.', 'error');
    return;
  }

  const contactName = log.contact?.name || 'Contacto';
  const contactEmail = log.contact?.email || '-';
  const companyName = log.client?.company && log.client.company !== '-' ? log.client.company : log.client?.name || '-';
  const accountSender = log.account ? `${log.account.name} (${log.account.email_address})` : 'Equipo Alerce';
  const repliedDate = log.replied_at ? new Date(log.replied_at).toLocaleString('es-AR') : 'Fecha no registrada';
  const sentDate = log.sent_at ? new Date(log.sent_at).toLocaleString('es-AR') : '-';
  const channel = log.reply_channel || 'email';
  const existingNotes = log.reply_notes || '';

  modalContainer.innerHTML = `
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-surface border border-border rounded-lg shadow-2xl w-full max-w-xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 text-xs">
        <!-- Header -->
        <div class="p-4 border-b border-border flex items-center justify-between bg-surface-subtle/50">
          <div class="flex items-center gap-2">
            <span class="p-1.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
              ${getIcon('messageSquare', 'w-4 h-4')}
            </span>
            <div>
              <h3 class="font-bold text-sm text-text-primary">Detalle de Respuesta del Prospecto</h3>
              <p class="text-[11px] text-text-secondary">Registro capturado vía ${channel} • ${repliedDate}</p>
            </div>
          </div>
          <button id="btn-close-reply-modal" class="p-1 text-text-secondary hover:text-text-primary rounded text-sm font-bold">✕</button>
        </div>

        <!-- Body -->
        <div class="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <!-- Prospect card -->
          <div class="p-3 bg-surface-subtle rounded-md border border-border/70 space-y-1.5">
            <div class="flex items-center justify-between">
              <span class="text-[10px] font-bold uppercase tracking-wider text-text-secondary">Prospecto / Destinatario</span>
              <span class="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                Respuesta Confirmada
              </span>
            </div>
            <div class="grid grid-cols-2 gap-2 text-[11px] pt-1">
              <div>
                <p class="text-text-secondary">Nombre:</p>
                <p class="font-bold text-text-primary">${contactName}</p>
              </div>
              <div>
                <p class="text-text-secondary">Empresa:</p>
                <p class="font-bold text-text-primary">${companyName}</p>
              </div>
              <div>
                <p class="text-text-secondary">Email:</p>
                <p class="font-mono text-text-primary">${contactEmail}</p>
              </div>
              <div>
                <p class="text-text-secondary">Remitente (Autor):</p>
                <p class="font-mono text-text-primary">${accountSender}</p>
              </div>
            </div>
          </div>

          <!-- Outbound Context -->
          <div class="p-3 bg-surface-subtle/60 rounded-md border border-border/50 text-[11px] space-y-1">
            <span class="text-[10px] font-bold uppercase tracking-wider text-text-secondary">Correo Saliente Enviado</span>
            <p class="font-semibold text-text-primary">"${log.subject}"</p>
            <p class="text-[10px] text-text-secondary font-mono">Enviado el ${sentDate} • ${log.campaign?.name || 'Envío Directo'}</p>
          </div>

          <!-- Inbound Response Box -->
          <div class="p-3.5 bg-emerald-50/70 border border-emerald-200/80 rounded-md space-y-2.5">
            <div class="flex items-center justify-between">
              <span class="font-bold text-emerald-800 text-[11px] flex items-center gap-1.5">
                ${getIcon('messageSquare', 'w-3.5 h-3.5 text-emerald-600')}
                <span>Mensaje / Respuesta Recibida</span>
              </span>
              <span class="text-[10px] font-mono text-emerald-700 font-semibold">${repliedDate}</span>
            </div>
            ${existingNotes ? `
              <div class="p-3 bg-surface border border-emerald-200/60 rounded text-xs text-text-primary whitespace-pre-wrap font-sans leading-relaxed max-h-48 overflow-y-auto select-text shadow-2xs">
                ${escapeHtml(existingNotes)}
              </div>
            ` : `
              <p class="text-[11px] text-emerald-900 leading-relaxed italic">
                Respuesta detectada en buzón de entrada (sin extracto previo capturado).
              </p>
            `}
            <p class="text-[10px] text-emerald-800">
              Secuencia automática detenida para este contacto. Ficha comercial sincronizada en el CRM.
            </p>
          </div>

          <!-- Reply Notes / Summary (Editable) -->
          <div class="space-y-1.5">
            <label class="block font-semibold text-text-primary text-[11px]">
              Notas Comerciales / Resumen de la Respuesta:
            </label>
            <textarea 
              id="reply-modal-notes" 
              rows="3" 
              placeholder="Escribe o complementa aquí notas sobre la respuesta del prospecto..." 
              class="w-full p-2.5 bg-surface-subtle border border-border rounded text-xs leading-relaxed outline-none focus:bg-surface focus:border-primary"
            >${escapeHtml(existingNotes)}</textarea>
            <p class="text-[10px] text-text-secondary">Estas notas se reflejan en el historial de la ficha del cliente en Comercial Clientes.</p>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-3.5 border-t border-border flex items-center justify-between bg-surface-subtle/40">
          <button 
            type="button" 
            id="btn-open-gmail-search" 
            class="px-3 py-1.5 rounded border border-border bg-surface hover:bg-surface-subtle text-text-primary font-medium flex items-center gap-1.5 transition-colors shadow-xs"
            title="Buscar el hilo de conversación en Gmail"
          >
            ${getIcon('externalLink', 'w-3 h-3 text-text-secondary')}
            <span>Buscar en Gmail</span>
          </button>

          <div class="flex items-center gap-2">
            <button type="button" id="btn-cancel-reply-modal" class="px-3 py-1.5 rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary">
              Cerrar
            </button>
            <button type="button" id="btn-save-reply-notes" class="px-4 py-1.5 rounded bg-primary text-white font-semibold hover:bg-primary-hover shadow-sm">
              Guardar Notas
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  // Bind modal buttons
  const close = () => {
    modalContainer.innerHTML = '';
  };

  document.getElementById('btn-close-reply-modal')?.addEventListener('click', close);
  document.getElementById('btn-cancel-reply-modal')?.addEventListener('click', close);

  document.getElementById('btn-open-gmail-search')?.addEventListener('click', () => {
    const searchUrl = `https://mail.google.com/mail/u/0/#search/${encodeURIComponent(log.subject)}`;
    window.open(searchUrl, '_blank');
  });

  document.getElementById('btn-save-reply-notes')?.addEventListener('click', async () => {
    const newNotes = document.getElementById('reply-modal-notes')?.value || '';
    const saveBtn = document.getElementById('btn-save-reply-notes');
    if (saveBtn) saveBtn.innerText = 'Guardando...';
    try {
      await DataService.updateEmailLog(log.id, { reply_notes: newNotes });
      log.reply_notes = newNotes;
      showToast('Notas de la respuesta guardadas correctamente.', 'success');
      close();
    } catch (err) {
      showToast(`Error al guardar notas: ${err.message}`, 'error');
      if (saveBtn) saveBtn.innerText = 'Guardar Notas';
    }
  });
}

// -------------------------------------------------------------
// MODAL DE VISTA COMPLETA DE PLANTILLA (DESKTOP & MOBILE RESPONSIVE)
// -------------------------------------------------------------
export function openFullTemplatePreviewModal(templateData) {
  const modalContainer = document.getElementById('marketing-modal-container') || document.body;

  const name = templateData?.name || 'Vista Previa de Plantilla';
  const rawSubject = templateData?.subject || 'Consulta sobre oportunidades comerciales';
  const rawBody = templateData?.body || '';
  const ctaText = templateData?.ctaText || '';
  const ctaUrl = templateData?.ctaUrl || '';

  // Variable replacements
  const contactName = 'Juan Pérez';
  const companyName = 'Acme Corp';
  const senderName = 'Martín Batarev';
  const senderEmail = 'martin@alerce.pro';

  const subjectReplaced = rawSubject
    .replace(/\{\{contact\.first_name\}\}/gi, 'Juan')
    .replace(/\{\{contact\.name\}\}/gi, contactName)
    .replace(/\{\{company\.name\}\}/gi, companyName)
    .replace(/\{\{sender\.name\}\}/gi, senderName);

  let content = rawBody
    .replace(/\{\{contact\.first_name\}\}/gi, 'Juan')
    .replace(/\{\{contact\.name\}\}/gi, contactName)
    .replace(/\{\{company\.name\}\}/gi, companyName)
    .replace(/\{\{sender\.name\}\}/gi, senderName);

  // Inyectar CTA buttons (soporta {{cta}}, {{cta:style}} y corchetes con estilos DESIGN.md)
  const ctaStyle = templateData?.ctaStyle || 'forest';
  let ctasList = [];
  if (Array.isArray(templateData?.ctas) && templateData.ctas.length > 0) {
    ctasList = templateData.ctas;
  } else if (ctaText && ctaUrl) {
    ctasList = [{ id: 'cta_1', text: ctaText, url: ctaUrl, style: ctaStyle }];
  }

  if (ctasList.length > 0) {
    content = injectCtasIntoEmailBody(content, ctasList);
  }

  const isHtml = /<(?:p|div|table|h[1-6]|ul|ol|blockquote|section|article|header|footer)[\s>]/i.test(content);
  let finalHtml = '';
  if (isHtml) {
    finalHtml = content;
  } else {
    finalHtml = content.split('\n\n').map(p => {
      const trimmed = p.trim();
      if (!trimmed) return '';
      if (trimmed.includes('data-cta-block="true"') || trimmed.includes('<table')) return trimmed;
      return `<p style="margin: 0 0 14px 0; line-height: 1.6; font-size: 14px;">${trimmed.replace(/\n/g, '<br/>')}</p>`;
    }).filter(Boolean).join('');
  }

  const modalEl = document.createElement('div');
  modalEl.id = 'full-template-preview-modal';
  modalEl.className = 'fixed inset-0 bg-black/70 backdrop-blur-xs z-50 flex items-center justify-center p-2 sm:p-4 animate-fade-in';
  modalEl.innerHTML = `
    <div class="bg-surface border border-border rounded-lg shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
      <!-- Modal Header -->
      <div class="p-3 sm:p-4 border-b border-border flex items-center justify-between bg-surface-subtle/60 shrink-0">
        <div class="flex items-center gap-2.5 min-w-0">
          <span class="p-1.5 rounded bg-surface border border-border text-primary shrink-0">
            ${getIcon('maximize', 'w-4 h-4')}
          </span>
          <div class="truncate">
            <h3 class="text-xs sm:text-sm font-bold text-text-primary truncate">${name}</h3>
            <p class="text-[10px] sm:text-[11px] text-text-secondary truncate">Vista previa responsive simulada de cliente de correo</p>
          </div>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <!-- Viewport Mode Switcher -->
          <div class="flex items-center bg-surface border border-border rounded p-0.5 text-xs">
            <button 
              type="button" 
              id="btn-preview-mode-desktop" 
              class="px-2.5 py-1 rounded font-medium transition-all flex items-center gap-1.5 bg-primary text-white font-semibold shadow-xs cursor-pointer"
              title="Vista de escritorio (600px standard email)"
            >
              ${getIcon('monitor', 'w-3.5 h-3.5')}
              <span class="hidden sm:inline">Escritorio (600px)</span>
            </button>
            <button 
              type="button" 
              id="btn-preview-mode-mobile" 
              class="px-2.5 py-1 rounded font-medium transition-all flex items-center gap-1.5 text-text-secondary hover:text-text-primary cursor-pointer"
              title="Vista móvil (375px pantalla smartphone)"
            >
              ${getIcon('smartphone', 'w-3.5 h-3.5')}
              <span class="hidden sm:inline">Móvil (375px)</span>
            </button>
          </div>

          <button 
            type="button" 
            id="btn-close-full-preview" 
            class="p-1.5 text-text-secondary hover:text-text-primary hover:bg-surface rounded transition-colors ml-1 cursor-pointer"
            title="Cerrar vista completa (Esc)"
          >
            ${getIcon('x', 'w-4 h-4')}
          </button>
        </div>
      </div>

      <!-- Simulated Email Client Headers -->
      <div class="bg-surface px-4 sm:px-6 py-3 border-b border-border text-xs space-y-1.5 shrink-0 bg-surface-subtle/30 font-sans">
        <div class="flex items-center gap-2">
          <span class="text-text-secondary w-14 font-medium text-[11px]">De:</span>
          <span class="font-semibold text-text-primary">${senderName} <span class="font-normal font-mono text-[11px] text-text-secondary">&lt;${senderEmail}&gt;</span></span>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-text-secondary w-14 font-medium text-[11px]">Para:</span>
          <span class="font-semibold text-text-primary">${contactName} <span class="font-normal font-mono text-[11px] text-text-secondary">&lt;juan@acmecorp.com&gt;</span></span>
        </div>
        <div class="flex items-baseline gap-2 pt-0.5">
          <span class="text-text-secondary w-14 font-medium text-[11px]">Asunto:</span>
          <span class="font-bold text-text-primary text-xs sm:text-sm font-mono">${subjectReplaced}</span>
        </div>
      </div>

      <!-- Preview Viewport Container -->
      <div class="flex-1 bg-zinc-100 overflow-y-auto overflow-x-hidden p-3 sm:p-8 flex justify-center items-start">
        <style>
          #email-preview-container {
            box-sizing: border-box !important;
            overflow-x: hidden !important;
          }
          #email-preview-container * {
            box-sizing: border-box !important;
          }
          #email-preview-container .email-body-rendered {
            max-width: 100% !important;
            overflow-wrap: break-word !important;
            word-break: break-word !important;
          }
          #email-preview-container .email-body-rendered img {
            max-width: 100% !important;
            height: auto !important;
            display: block !important;
          }
          #email-preview-container .email-body-rendered table {
            max-width: 100% !important;
            width: 100% !important;
            table-layout: fixed !important;
            word-break: break-word !important;
          }
          #email-preview-container .email-body-rendered td,
          #email-preview-container .email-body-rendered th {
            word-break: break-word !important;
            overflow-wrap: break-word !important;
          }
          #email-preview-container .email-body-rendered pre,
          #email-preview-container .email-body-rendered code {
            white-space: pre-wrap !important;
            word-break: break-all !important;
            max-width: 100% !important;
          }
          #email-preview-container .email-body-rendered div,
          #email-preview-container .email-body-rendered p,
          #email-preview-container .email-body-rendered section {
            max-width: 100% !important;
          }
        </style>
        <div 
          id="email-preview-container" 
          class="w-full max-w-[600px] bg-white rounded-md shadow-sm border border-zinc-200/90 p-6 sm:p-8 space-y-4 text-zinc-900 transition-all duration-300 font-sans leading-relaxed box-border overflow-hidden"
        >
          <div class="email-body-rendered w-full max-w-full overflow-hidden break-words">
            ${finalHtml || '<p class="text-zinc-400 italic">Sin contenido redactado en el cuerpo...</p>'}
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="p-3 border-t border-border flex items-center justify-between bg-surface-subtle/50 text-[11px] text-text-secondary shrink-0">
        <div class="flex items-center gap-2">
          <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
          <span>Renderizado conforme a estándares de compatibilidad HTML email</span>
        </div>
        <button 
          type="button" 
          id="btn-footer-close-full-preview" 
          class="px-3 py-1.5 rounded border border-border bg-surface hover:bg-surface-subtle text-text-primary font-medium transition-colors cursor-pointer"
        >
          Cerrar Vista Previa
        </button>
      </div>
    </div>
  `;

  modalContainer.appendChild(modalEl);

  const closeModal = () => {
    modalEl.remove();
  };

  document.getElementById('btn-close-full-preview')?.addEventListener('click', closeModal);
  document.getElementById('btn-footer-close-full-preview')?.addEventListener('click', closeModal);
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) closeModal();
  });

  const escHandler = (e) => {
    if (e.key === 'Escape') {
      closeModal();
      window.removeEventListener('keydown', escHandler);
    }
  };
  window.addEventListener('keydown', escHandler);

  // Switcher Desktop / Mobile
  const btnDesktop = document.getElementById('btn-preview-mode-desktop');
  const btnMobile = document.getElementById('btn-preview-mode-mobile');
  const previewBox = document.getElementById('email-preview-container');

  btnDesktop?.addEventListener('click', () => {
    btnDesktop.className = 'px-2.5 py-1 rounded font-medium transition-all flex items-center gap-1.5 bg-primary text-white font-semibold shadow-xs cursor-pointer';
    btnMobile.className = 'px-2.5 py-1 rounded font-medium transition-all flex items-center gap-1.5 text-text-secondary hover:text-text-primary cursor-pointer';
    if (previewBox) {
      previewBox.className = 'w-full max-w-[600px] bg-white rounded-md shadow-sm border border-zinc-200/90 p-6 sm:p-8 space-y-4 text-zinc-900 transition-all duration-300 font-sans leading-relaxed box-border overflow-hidden';
    }
  });

  btnMobile?.addEventListener('click', () => {
    btnMobile.className = 'px-2.5 py-1 rounded font-medium transition-all flex items-center gap-1.5 bg-primary text-white font-semibold shadow-xs cursor-pointer';
    btnDesktop.className = 'px-2.5 py-1 rounded font-medium transition-all flex items-center gap-1.5 text-text-secondary hover:text-text-primary cursor-pointer';
    if (previewBox) {
      previewBox.className = 'w-full max-w-[375px] bg-white rounded-3xl shadow-2xl border-[6px] border-zinc-800 p-4 sm:p-5 space-y-4 text-zinc-900 transition-all duration-300 font-sans leading-relaxed box-border overflow-hidden';
    }
  });
}

// -------------------------------------------------------------
// MODAL PARA INSERTAR RECURSO DE STORAGE EN PLANTILLA
// -------------------------------------------------------------
export async function openInsertResourceModal() {
  const modalContainer = document.getElementById('marketing-modal-container') || document.body;

  let resources = [];
  try {
    resources = await DataService.listResources();
  } catch (err) {
    showToast('Error cargando recursos de Storage: ' + err.message, 'error');
    return;
  }

  const modalEl = document.createElement('div');
  modalEl.id = 'insert-resource-modal';
  modalEl.className = 'fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-fade-in';
  modalEl.innerHTML = `
    <div class="bg-surface border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden text-xs">
      <div class="p-4 border-b border-border flex items-center justify-between bg-surface-subtle/60">
        <div class="flex items-center gap-2">
          <span class="p-1 rounded bg-surface border border-border text-primary">
            ${getIcon('image', 'w-4 h-4')}
          </span>
          <h3 class="font-bold text-sm text-text-primary">Insertar Recurso en la Plantilla</h3>
        </div>
        <button id="btn-close-insert-res" class="p-1 text-text-secondary hover:text-text-primary rounded cursor-pointer">${getIcon('x', 'w-4 h-4')}</button>
      </div>

      <div class="p-4 overflow-y-auto max-h-[60vh] space-y-3">
        <p class="text-text-secondary text-[11px]">
          Selecciona una imagen de tu almacenamiento para insertarla con etiquetas HTML listas para correo en la posición actual de tu cursor:
        </p>

        ${resources.length === 0 ? `
          <div class="p-8 text-center bg-surface-subtle rounded border border-dashed border-border space-y-2">
            <p class="font-semibold text-text-primary">No tienes recursos subidos aún</p>
            <p class="text-text-secondary text-[11px]">Ve a la sección <strong>Recursos</strong> en el menú lateral para subir imágenes o logos.</p>
            <a href="#/resources" class="inline-block mt-2 px-3 py-1.5 rounded bg-primary text-white font-semibold text-xs">Ir a Recursos</a>
          </div>
        ` : `
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
            ${resources.map(r => {
              const isImg = /\.(png|jpe?g|webp|svg|gif)$/i.test(r.name);
              return `
                <div class="res-item-select p-2 rounded border border-border hover:border-primary hover:bg-surface-subtle/50 cursor-pointer transition-all flex flex-col justify-between" data-url="${r.publicUrl}" data-name="${r.name}" data-is-img="${isImg}">
                  <div class="w-full h-24 bg-surface-subtle rounded border border-border/50 overflow-hidden flex items-center justify-center mb-1.5">
                    ${isImg ? `
                      <img src="${r.publicUrl}" alt="${r.name}" class="w-full h-full object-contain p-1" />
                    ` : `
                      <div class="flex flex-col items-center gap-1 text-purple-700">
                        ${getIcon('fileText', 'w-6 h-6')}
                        <span class="text-[9px] font-mono">PDF</span>
                      </div>
                    `}
                  </div>
                  <p class="text-[11px] font-bold text-text-primary truncate" title="${r.name}">${r.name}</p>
                  <span class="text-[10px] text-primary font-semibold mt-1">Insertar &rarr;</span>
                </div>
              `;
            }).join('')}
          </div>
        `}
      </div>

      <div class="p-3 border-t border-border flex justify-end bg-surface-subtle/40">
        <button id="btn-cancel-insert-res" class="px-3 py-1.5 rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary cursor-pointer">
          Cancelar
        </button>
      </div>
    </div>
  `;

  modalContainer.appendChild(modalEl);

  const close = () => modalEl.remove();
  document.getElementById('btn-close-insert-res')?.addEventListener('click', close);
  document.getElementById('btn-cancel-insert-res')?.addEventListener('click', close);

  modalEl.querySelectorAll('.res-item-select').forEach(card => {
    card.addEventListener('click', () => {
      const url = card.dataset.url;
      const name = card.dataset.name;
      const isImg = card.dataset.isImg === 'true';

      const bodyTextarea = document.getElementById('tpl-body');
      if (bodyTextarea) {
        const snippet = isImg 
          ? `<img src="${url}" alt="${name}" style="max-width: 100%; height: auto; display: block; margin: 16px 0;" />\n`
          : `<p><a href="${url}" target="_blank" style="color: #18181b; font-weight: 600; text-decoration: underline;">Descargar ${name} (PDF)</a></p>\n`;

        const start = bodyTextarea.selectionStart || 0;
        const end = bodyTextarea.selectionEnd || 0;
        const currentVal = bodyTextarea.value;
        bodyTextarea.value = currentVal.substring(0, start) + snippet + currentVal.substring(end);
        
        // Trigger live preview update
        bodyTextarea.dispatchEvent(new Event('input'));
        showToast('¡Recurso insertado en la plantilla!', 'success');
      }
      close();
    });
  });
}

// -------------------------------------------------------------
// MODAL DE GUÍA & AYUDA INTEGRAL
// -------------------------------------------------------------
function openHelpGuideModal() {
  const modalContainer = document.getElementById('marketing-modal-container');
  if (!modalContainer) return;

  modalContainer.innerHTML = `
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-surface border border-border rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <!-- Header -->
        <div class="p-4 border-b border-border flex items-center justify-between bg-surface-subtle/50">
          <div class="flex items-center gap-2">
            <span class="p-1 rounded bg-white border border-border">${getIcon('book', 'w-4 h-4 text-primary')}</span>
            <div>
              <h3 class="font-bold text-sm text-text-primary">Guía de Operación y Configuración: Email Marketing</h3>
              <p class="text-[11px] text-text-secondary">Manual de configuración de cuentas Google, motor de secuencias y buenas prácticas comerciales</p>
            </div>
          </div>
          <button id="btn-close-help-modal" class="p-1 text-text-secondary hover:text-text-primary rounded">✕</button>
        </div>

        <!-- Body -->
        <div class="p-6 overflow-y-auto space-y-6 text-xs leading-relaxed text-text-primary">
          <!-- 1. Cuentas de Google -->
          <div class="space-y-2.5 p-4 rounded-md bg-surface border border-border">
            <h4 class="font-bold text-xs uppercase tracking-wider text-primary flex items-center gap-1.5">
              ${getIcon('settings', 'w-3.5 h-3.5')}
              <span>1. Cómo configurar una Cuenta de Envío de Google</span>
            </h4>
            <p>El sistema te permite enviar correos tanto desde tu dominio institucional (ej. <code>contacto@equipoalerce.com</code>) como desde tu casilla personal (ej. <code>@gmail.com</code>):</p>
            
            <div class="space-y-2 pt-1">
              <div class="p-3 bg-surface-subtle rounded border border-border/60 space-y-1">
                <p class="font-semibold text-text-primary">Opción A: Contraseña de Aplicación de Google (Obligatorio para casillas personales @gmail.com)</p>
                <p class="text-text-secondary text-[11px]">Google prohíbe las Service Accounts en cuentas <code>@gmail.com</code> personales (devuelve <code>unauthorized_client</code>). Para usar tu Gmail personal de forma 100% confiable y segura:</p>
                <ol class="list-decimal list-inside space-y-1 text-text-secondary text-[11px] pt-1">
                  <li>Ingresa a <a href="https://myaccount.google.com/apppasswords" target="_blank" class="text-primary underline font-bold">myaccount.google.com/apppasswords</a> (requiere tener activa la <i>Verificación en 2 pasos</i> en Seguridad).</li>
                  <li>En el campo de nombre de app, escribe <code>Business OS</code> y haz clic en <b>Crear</b>.</li>
                  <li>Google te mostrará una contraseña amarilla de <b>16 caracteres</b> (ej: <code>abcd efgh ijkl mnop</code>).</li>
                  <li>Copia esa contraseña, ve a <b>Cuentas & Cuotas > Configurar</b> en Business OS, selecciona <b>Contraseña de Aplicación</b> y pégala. El sistema enviará a través de TLS seguro (puerto 465) sin bloqueos.</li>
                </ol>
              </div>

              <div class="p-3 bg-surface-subtle rounded border border-border/60 space-y-1">
                <p class="font-semibold text-text-primary">Opción B: Google Service Account (Exclusivo para dominios Google Workspace como @equipoalerce.com)</p>
                <ol class="list-decimal list-inside space-y-1 text-text-secondary text-[11px]">
                  <li>Ingresa a <a href="https://console.cloud.google.com" target="_blank" class="text-primary underline">console.cloud.google.com</a> con el proyecto de tu organización.</li>
                  <li>En el menú lateral, ve a <b>APIs y Servicios > Biblioteca</b> y habilita la <b>Gmail API</b>.</li>
                  <li>Ve a <b>IAM y administración > Cuentas de servicio</b> y crea una cuenta de servicio.</li>
                  <li>En la pestaña <b>Claves (Keys)</b>, haz clic en <i>Agregar clave > JSON</i> para descargar el archivo de credenciales.</li>
                  <li><b>Paso crítico en Workspace:</b> En la consola de administración (<a href="https://admin.google.com" target="_blank" class="text-primary underline">admin.google.com</a>) ve a <i>Seguridad > Controles de API > Delegación en todo el dominio</i> y autoriza el Client ID de la Service Account con el alcance <code>https://www.googleapis.com/auth/gmail.send</code>.</li>
                  <li>Pega el contenido JSON descargado en Business OS seleccionando el método <b>Google Service Account</b>.</li>
                </ol>
              </div>
            </div>
          </div>

          <!-- 2. Motor de Envíos y Automatización -->
          <div class="space-y-2 p-4 rounded-md bg-surface border border-border">
            <h4 class="font-bold text-xs uppercase tracking-wider text-primary flex items-center gap-1.5">
              ${getIcon('send', 'w-3.5 h-3.5')}
              <span>2. Cómo funciona el Motor de Envíos y Secuencias</span>
            </h4>
            <p>El motor trabaja de forma autónoma mediante <b>Supabase Edge Functions</b> y sigue estas reglas operativas:</p>
            <ul class="list-disc list-inside space-y-1 text-text-secondary text-[11px]">
              <li><b>Límites de Cuota:</b> Cada cuenta tiene un límite diario (por defecto 100 correos). Si se alcanza, el sistema frena automáticamente para evitar advertencias de Google.</li>
              <li><b>Monitoreo Finito (30 Días):</b> Para no consultar la API de Gmail de forma perpetua, los hilos de conversación se monitorean solo durante 30 días posteriores al envío.</li>
              <li><b>Detección de Respuestas:</b> Al detectar una respuesta (o registrarla manualmente desde la ficha), el contacto <b>sale de inmediato de la secuencia</b> para no seguir enviándole correos automáticos.</li>
              <li><b>Supresión Absoluta (No Contactar):</b> Si un contacto o empresa está marcado como "No contactar", ninguna campaña podrá enviarle un correo jamás.</li>
            </ul>
          </div>

          <!-- 3. Las Cuatro Secciones -->
          <div class="space-y-2 p-4 rounded-md bg-surface border border-border">
            <h4 class="font-bold text-xs uppercase tracking-wider text-primary flex items-center gap-1.5">
              ${getIcon('kanban', 'w-3.5 h-3.5')}
              <span>3. Las 4 Secciones del Módulo</span>
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
              <div class="p-2.5 bg-surface-subtle rounded border border-border/60">
                <p class="font-semibold text-text-primary text-[11px]">1. Campañas & Secuencias</p>
                <p class="text-[10px] text-text-secondary">Permite crear envíos masivos basados en filtros del CRM (etapa comercial, días sin contacto, etc.) con secuencias escalonadas (Día 0 → Día 15 → Día 30).</p>
              </div>
              <div class="p-2.5 bg-surface-subtle rounded border border-border/60">
                <p class="font-semibold text-text-primary text-[11px]">2. Plantillas & CTAs</p>
                <p class="text-[10px] text-text-secondary">Editor dual Markdown/HTML con tags dinámicos (<code>{{contact.first_name}}</code>, <code>{{company.name}}</code>), botones CTA con tracking opcional y previsualización en vivo.</p>
              </div>
              <div class="p-2.5 bg-surface-subtle rounded border border-border/60">
                <p class="font-semibold text-text-primary text-[11px]">3. Cuentas & Cuotas</p>
                <p class="text-[10px] text-text-secondary">Monitoreo del consumo diario de envíos, advertencias visuales de salud de cuenta y conexión segura de claves.</p>
              </div>
              <div class="p-2.5 bg-surface-subtle rounded border border-border/60">
                <p class="font-semibold text-text-primary text-[11px]">4. Estadísticas & Auditoría</p>
                <p class="text-[10px] text-text-secondary">Embudo comercial real (Respuestas → Diagnósticos → Propuestas → Ganados) y tabla detallada con cada email individual, aperturas y clicks.</p>
              </div>
            </div>
          </div>

          <!-- 4. Acciones desde la Ficha del Cliente -->
          <div class="space-y-2 p-4 rounded-md bg-surface border border-border">
            <h4 class="font-bold text-xs uppercase tracking-wider text-primary flex items-center gap-1.5">
              ${getIcon('users', 'w-3.5 h-3.5')}
              <span>4. Reactivación Directa vs Campañas Masivas</span>
            </h4>
            <p class="text-text-secondary">No necesitas crear una campaña compleja para un solo cliente. Desde la vista de <b>Comercial & Clientes</b>:</p>
            <ul class="list-disc list-inside space-y-1 text-text-secondary text-[11px]">
              <li><b>Botón "Reactivar":</b> Abre un modal express para enviarle un email en el acto usando una plantilla existente a ese contacto puntual.</li>
              <li><b>Botón "Respuesta":</b> Si el cliente te respondió por WhatsApp, llamada o reunión, haz clic en este botón para detener de inmediato cualquier secuencia automática activa y avanzar su etapa comercial.</li>
            </ul>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-4 border-t border-border flex justify-end bg-surface-subtle/50">
          <button type="button" id="btn-ok-help-modal" class="px-4 py-2 text-xs font-semibold rounded bg-primary text-white hover:bg-primary-hover shadow-sm">
            Entendido
          </button>
        </div>
      </div>
    </div>
  `;

  const close = () => { modalContainer.innerHTML = ''; };
  document.getElementById('btn-close-help-modal')?.addEventListener('click', close);
  document.getElementById('btn-ok-help-modal')?.addEventListener('click', close);
}
